import { z } from 'zod';
import { centavos, pageQuery } from './envelope.ts';
/* The roster filters on where a player lives, so it borrows the vocabulary
   registration already validates against rather than defining a second one.
   A filter that accepted a code the platform refuses to STORE would be a
   control that can only ever return nothing. */
import { AUDIT_REASON_MIN_LENGTH } from './audit-reasons.ts';
import { residenceFilter } from './residency.ts';
import { brandName } from './platform-admin.ts';
/* The create form validates a game's category against the SAME enum the player
   catalogue filters on, rather than a second list that could drift from it. */
import { gameCategory } from './games.ts';
/* A game picture's allowed types and ceiling, from the browser-safe file the
   picker also reads, so a ticket and a picker cannot disagree. */
import { GAME_PICTURE_SOURCES, GAME_PICTURE_TYPES, MAX_GAME_PICTURE_BYTES } from './game-picture.ts';
/* The payout closed sets from their leaf file, never from payout-accounts.ts: that
   file's imports lead back here through player-auth.ts, and the cycle stops this
   one evaluating (payout-closed-sets.ts says how). */
import { nameMatch, parkedReason, payoutAccountStatus, payoutState } from './payout-closed-sets.ts';

/* ── Players ─────────────────────────────────────────────────────────────── */

/**
 * One brand's relationship to one title, as the games list reports it.
 *
 * `brandId` is the identity and the only field a client may branch on. `brand`
 * is the operator's display name, carried so a screen need not hold a second
 * map, and it is NOT a key: two brands may legitimately share a name, and the
 * name may contain any character -- including the `', '` that used to be this
 * boundary's delimiter, which is the whole reason this shape exists.
 */
export const gameBrandShelfEntry = z.object({
  brandId: z.string().min(1).max(64),
  /* `brandName` itself, not a copy of its rules. Restating
     `.trim().min(2).max(120)` here would make one truth into two declarations,
     which is the defect this change exists to remove. */
  brand: brandName,
  /**
   * `game.game_brand.enabled` for this pair. A pair with NO row in
   * `game.game_brand` is ABSENT from the array -- never considered is not the
   * same fact as switched off, and the write contract already draws that
   * distinction with `from: boolean | null`. An absent pair is not
   * `enabled: false`.
   */
  enabled: z.boolean(),
});
export type GameBrandShelfEntry = z.infer<typeof gameBrandShelfEntry>;

/**
 * THE SHELF, WITHIN THE CALLER'S BRAND SCOPE -- which is why the name says so.
 *
 * The query filters to the caller's allowed/selected brands, so for a
 * restricted operator this is only those. An empty array means "on none of YOUR
 * brands", never "on no shelf anywhere" -- the reading `enabledBrandCountInScope`
 * is named for in the write contract, and the reason a platform-wide figure is
 * deliberately absent here: telling a restricted operator how many OTHER brands
 * carry a title is a disclosure even when it names none of them.
 *
 * Order is not significant. Sort for display; do not depend on it.
 */
export const gameBrandShelf = z.array(gameBrandShelfEntry);

/**
 * A row of `GET /api/games` -- THE FIRST RESPONSE SCHEMA THIS ROUTE HAS HAD.
 *
 * It was declared twice before, independently and by hand: once as a SQL SELECT
 * list and once as a TS interface in the component, with nothing reconciling
 * them. That is why a set could be shipped as a delimited string without anyone
 * noticing it was lossy -- there was no schema in which to say otherwise.
 *
 * SNAKE_CASE IS THE EXISTING WIRE SHAPE, NOT A PREFERENCE. These names are the
 * raw `pg` column aliases the route has always returned, and renaming them is a
 * second breaking change with no user behind it. `brandShelfInScope` and
 * `brandCountInScope` are the two new fields and both follow the `...InScope`
 * convention the write contract set.
 *
 * The numeric columns are TEXT on the wire on purpose: centavos and basis
 * points are exact integers that JSON's double would round, so they are cast to
 * text in SQL and parsed where they are used.
 */
export const gameListRow = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  category: z.string(),
  status: z.string(),
  /* NULLABLE, and the hand-written interface this replaces said `string`.
     `game.icon_name` has always been nullable; nothing coalesces it. The
     optimistic type was only ever true by luck of the seed. */
  icon_name: z.string().nullable(),
  /** Basis points: 9640 is 96.4%. Null when the provider publishes no figure. */
  declared_rtp_bp: z.string().nullable(),
  /**
   * The provider's own type word for a live-casino table — `bac`, `rot`, `dtx`
   * — and null for everything that is not one. Nullable and NOT coalesced to a
   * word: "this provider publishes no table type" is a different fact from any
   * label, and the create and edit forms have to be able to tell them apart to
   * know whether an empty box means "clear it" or "there was never one".
   */
  sub_category: z.string().nullable(),
  provider: z.string(),
  provider_code: z.string(),
  provider_status: z.string(),
  /**
   * `game.game_provider.driver` — MOCK · LIVE · SA.
   *
   * On the LIST because the drawer's edit form needs it, and needs it BY
   * DRIVER rather than by provider code: a provider's hourly catalogue import
   * owns the title's name and table type, so those two boxes are read-only on
   * an SA-driven title and the form says why. Keyed on the driver so a second
   * provider put on the SA driver inherits the rule, and so the day a JILI
   * importer ships its driver joins the same list.
   */
  provider_driver: z.string(),
  brandShelfInScope: gameBrandShelf,
  /**
   * Active brands within the CALLER'S scope -- unscoped operator: every
   * active brand on the platform; scoped operator: their
   * identity.admin_brand_scope rows. Same name and same semantics as
   * `setGameBrandsResult.brandCountInScope` (the PUT response) on purpose:
   * one denominator, one source, computed the same way in both places so the
   * two never drift. Does NOT vary per game -- every row in one response
   * carries the same value, because it answers "how big is the caller's
   * scope", not "how many of THIS game's brands are in it" (that's
   * `brandShelfInScope`).
   */
  brandCountInScope: z.number().int().nonnegative(),
  rounds: z.string(),
  /** Centavos. */
  ggr: z.string(),
  /** Basis points, or null when nothing settled in the window. */
  measured_rtp_bp: z.string().nullable(),
  /**
   * The picture players see: an upload, else the provider's, else null and the
   * tile draws initials.
   */
  iconUrl: z.string().nullable(),
  /** The provider's own picture, kept aside while an upload shows. */
  providerIconUrl: z.string().nullable(),
  /**
   * Null: no operator has touched the picture. PROVIDER: an operator went back
   * to the provider's. UPLOADED: an upload is what players see.
   */
  iconSource: z.enum(GAME_PICTURE_SOURCES).nullable(),
  /** When an operator last uploaded or went back. Never set by an import. */
  iconUpdatedAt: z.iso.datetime().nullable(),
});
export type GameListRow = z.infer<typeof gameListRow>;

export const gameListResult = z.object({
  items: z.array(gameListRow),
  /**
   * False when this installation has no picture store (object storage with no
   * picture bucket): the console turns Upload off and says so. Going back to
   * the provider's picture still works.
   */
  pictureUploadsReady: z.boolean(),
});
export type GameListResult = z.infer<typeof gameListResult>;

export const playerStatus = z.enum(['ACTIVE', 'FLAGGED', 'RESTRICTED', 'SUSPENDED']);
export const kycStatus = z.enum(['UNVERIFIED', 'PENDING', 'VERIFIED', 'REJECTED']);
export const playerTier = z.enum(['BRONZE', 'SILVER', 'GOLD', 'VIP_3', 'VIP_4']);

/**
 * The six orders the roster can be read in.
 *
 * PLAYER and BALANCE were absent for as long as the route had no SQL for them,
 * and the screen rendered both options `disabled` rather than sorting the eight
 * rows on the page — which would have reordered one page and misrepresented
 * every other. They are here now because the route has a fixed fragment for
 * each; a key that is not in this enum still never reaches an ORDER BY.
 *
 * BALANCE orders by `wallet.player_cash_balance(p.id)` — the FUNCTION, not the
 * `cash_balance` alias, which is text-cast for transport. Ordering by that
 * alias is how ₱9,993.85 outranked ₱3,419,999.94 on this platform once already.
 */
export const playerSortKey = z.enum([
  'LAST_SEEN',
  'DEPOSITS',
  'GGR',
  'REGISTERED',
  'PLAYER',
  'BALANCE',
]);
export type PlayerSortKey = z.infer<typeof playerSortKey>;

/**
 * Which way the sort runs.
 *
 * Every key used to be hard-coded DESC, so "oldest registration first" and
 * "smallest balance first" — the two questions a dormancy sweep and a
 * reconciliation actually ask — could not be expressed at all. The direction is
 * a closed set for the same reason the key is: it selects a fixed SQL fragment
 * and never becomes syntax itself.
 */
export const sortDirection = z.enum(['ASC', 'DESC']);
export type SortDirection = z.infer<typeof sortDirection>;

export const playerListQuery = pageQuery.extend({
  /** "Search matches player ID, display name, phone (with or without +63), email and agent code." */
  q: z.string().max(200).optional(),
  brandId: z.string().optional(),
  status: playerStatus.optional(),
  kycStatus: kycStatus.optional(),
  tier: playerTier.optional(),
  agentRef: z.string().optional(),
  /**
   * Where the player lives — one ISO 3166-1 alpha-2 code, or `UNKNOWN`.
   *
   * `UNKNOWN` is NOT a country and is not stored as one: it selects the players
   * whose `residence_country` is NULL because they registered before the column
   * existed. That population is the one an operator has to work through if the
   * licence ever requires a residency rule, so it has to be selectable — and it
   * cannot be spelled as a code, which is why the sentinel is seven letters.
   */
  residenceCountry: residenceFilter.optional(),
  /**
   * Whether to show accounts the operator has marked as test accounts.
   *
   * TRI-STATE, NOT A BOOLEAN, and that is the whole design of it. The default
   * has to be "everything", because a roster that silently hid a population
   * would make every count on the screen disagree with the database. A boolean
   * gives `undefined` two jobs — "no opinion" and "false" — and the day
   * somebody reads the second meaning into the first, test accounts vanish
   * from the list without anybody choosing that.
   *
   * It exists because of PAGCOR item 33(c): a marked account's play, deposits
   * and withdrawals leave Gross Gaming Revenue, including retroactively. "Which
   * accounts are excluded from the figures?" is the first question an
   * inspection asks about that, and until this filter the only way to answer it
   * was to open profiles one at a time.
   */
  testAccount: z.enum(['ANY', 'ONLY', 'EXCLUDED']).default('ANY'),
  sort: playerSortKey.default('LAST_SEEN'),
  direction: sortDirection.default('DESC'),
});
export type PlayerListQuery = z.infer<typeof playerListQuery>;

export const playerRow = z.object({
  id: z.string(),
  ref: z.string(),
  name: z.string(),
  brandName: z.string(),
  agentRef: z.string().nullable(),
  tier: playerTier,
  kycStatus,
  status: playerStatus,
  /** Cash only. Bonus and locked funds are shown in the drawer, not the list. */
  cashBalance: centavos,
  riskScore: z.number().int().min(0).max(100),
  lastSeenAt: z.iso.datetime().nullable(),
  /**
   * Lifetime, not period. The list query has always computed both — settled
   * deposits and non-voided GGR — and the handler dropped them on the floor,
   * so the column the design puts in the table had no source and the drawer
   * tile beside it read "not on this route".
   */
  lifetimeDeposits: centavos,
  lifetimeGgr: centavos,
  /**
   * NULL means NEVER ASKED, and every renderer of this field has to say so.
   *
   * The column landed with registration; accounts that predate it carry no
   * answer, and inventing one would be fabricating the fact the column exists
   * to record. A blank cell reads as an answer, so the roster prints the words
   * rather than an empty space.
   */
  residenceCountry: z.string().nullable(),
  /**
   * Marked as a test account — PAGCOR item 33(c).
   *
   * On the ROW rather than only behind the filter, so the fact is visible
   * without anybody having to go looking for it. An operator scanning the
   * roster should be able to see that one of these accounts is not counted in
   * the revenue figures; needing to turn a filter on to discover that is how a
   * marked account gets treated as a real player.
   */
  isTest: z.boolean(),
  /** Why it was marked. Null only on rows that are not test accounts. */
  testReason: z.string().nullable(),
});
export type PlayerRow = z.infer<typeof playerRow>;

/* ── Withdrawal queue ────────────────────────────────────────────────────── */

export const withdrawalStatus = z.enum([
  'AWAITING_CHECKER',
  'HELD_BY_RISK',
  'RELEASED',
  'REJECTED',
  'FAILED',
]);

/**
 * The reasons a withdrawal may be refused.
 *
 * A closed set, not free text: the rejection reason drives the message the
 * player is shown and the report that counts refusals by cause, and neither
 * can group prose. `withdrawal_rejection_has_reason` in the database refuses a
 * REJECTED row without one, so an endpoint that forgot to ask could not write
 * the row either.
 */
export const withdrawalRejectionReason = z.enum([
  'KYC_INCOMPLETE',
  'RAIL_MISMATCH',
  'SUSPECTED_FRAUD',
  'BONUS_WAGERING_UNMET',
  'DUPLICATE_REQUEST',
  'PLAYER_REQUEST',
]);
export type WithdrawalRejectionReason = z.infer<typeof withdrawalRejectionReason>;

export const releaseWithdrawalRequest = z.object({
  /** Optional, but recorded when given — a release can carry context too. */
  note: z.string().max(500).optional(),
});

export const rejectWithdrawalRequest = z.object({
  reasonCode: withdrawalRejectionReason,
  /* The player is told why. A code alone reads as a form number, so the note is
     required and long enough to say something useful. */
  note: z.string().min(10).max(500),
});

export const withdrawalQueueQuery = pageQuery.extend({
  brandId: z.string().optional(),
  status: withdrawalStatus.optional(),
  /**
   * Only the payouts waiting for an operator, whatever their status — a parked
   * payout was released, so the queue's own status filter would never find it.
   * `status` is ignored when this is set. Oldest park first.
   */
  parked: z.enum(['true']).optional(),
  /**
   * One request by its reference, whatever its status or park: the console's
   * review drawer reads its row again with it after every action (an action
   * moves the row out of the list the drawer was opened from), and a link to a
   * withdrawal opens one with it. `status` and `parked` stand aside when it is
   * set; the brand scope does not, so another brand's reference answers an
   * empty page. Only the platform's own shape is asked about — `WDL-91020`,
   * `WDL-B1199-1`.
   */
  ref: z
    .string()
    .max(32)
    .regex(/^WDL-[A-Z0-9-]+$/i, 'A withdrawal reference looks like WDL-91020.')
    .optional(),
  /**
   * One player's requests, newest first, whatever their status or park: the review drawer lists
   * the player's other requests and how each one ended with it. `status` and `parked` stand aside
   * when it is set; the brand scope does not, so another brand's player answers an empty page.
   * Only a player id in the platform's own shape is asked about.
   */
  playerId: z.uuid().optional(),
});

export const withdrawalRow = z.object({
  id: z.string(),
  ref: z.string(),
  playerId: z.string(),
  playerRef: z.string(),
  playerName: z.string(),
  brandName: z.string(),
  method: z.string(),
  amount: centavos,
  status: withdrawalStatus,
  /**
   * THE MASK IS ALL THIS WIRE CARRIES. The full number is encrypted in the payout
   * account vault (payout-vault.ts, under PAYOUT_VAULT_KEY) and read only when a
   * StarPay payout is handed over, each read recorded. No screen reveals it, by
   * design: a reveal route would be a feature that needed the breach first. Where
   * the platform does not send (a rail that only records, or a request raised
   * before registered accounts, which kept these four digits and nothing more),
   * the payouts team confirms the number with the player.
   */
  railAccountMask: z.string().nullable(),
  requestedAt: z.iso.datetime(),
  slaDueAt: z.iso.datetime(),
  waitingMinutes: z.number().int().nonnegative(),
  breachingSla: z.boolean(),
  heldReason: z.string().nullable(),
  /** Lifetime withdrawals ÷ lifetime deposits, in basis points. Over 9000 holds. */
  payoutRatioBp: z.number().int().nullable(),
  /**
   * TWO OF THE DESIGN'S SIX PRE-RELEASE CHECKS, and they are facts rather than
   * verdicts.
   *
   * `openBonusGrants` / `wageringOutstanding` answer "wagering complete": how
   * many ACTIVE grants this player holds and how much of their requirement is
   * still to run. `otherOpenRequests` / `otherOpenSameAmount` answer "duplicate
   * request": other payouts by the same player still in the queue, and how many
   * of those are for the identical figure — which is the shape that actually
   * reads as a double submission.
   *
   * NEITHER GATES ANYTHING. No rule on this platform forfeits a bonus when a
   * player withdraws, and two genuine payouts in one day are not a defect, so a
   * check that held the row by itself would be a rule nobody decided. They are
   * shown to the checker, who decides.
   *
   * A zero here is a real zero: the columns are coalesced in SQL, so "nothing
   * open" never arrives as the same value as "not looked up".
   */
  openBonusGrants: z.number().int().nonnegative(),
  wageringOutstanding: centavos,
  otherOpenRequests: z.number().int().nonnegative(),
  otherOpenSameAmount: z.number().int().nonnegative(),
  /**
   * OPEN risk alerts against this player, and the worst severity among them.
   *
   * THE COUNT, NEVER THE ALERTS. Which rule fired and on what evidence is behind
   * `players.restrict` and stays there — `GET /api/risk/alerts` is unchanged.
   * What crosses to a `transactions.view` holder is that an alert is open and how
   * serious it is, which is the difference between a checker deciding a payout
   * informed and deciding it blind. A count with no detail cannot identify a
   * rule, a threshold or another player.
   *
   * OPEN only: a closed alert was looked at and answered, and counting it would
   * hold a payout over work somebody already did.
   *
   * `worstAlertSeverity` is null when the count is zero — and also when a
   * severity outside the four known values reaches the column, which the screen
   * says out loud rather than rendering as blank.
   */
  openAlerts: z.number().int().nonnegative(),
  worstAlertSeverity: z.enum(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']).nullable(),
  initiatedBy: z.string().nullable(),
  /**
   * WHO RAISED IT AND WHO DECIDED IT, by name, for the review drawer.
   *
   * `makerName` is the admin who raised the request. It is null when the player raised it — their
   * own id is `initiatedBy` then, which is how the platform tells the two kinds of maker apart —
   * and when nobody is on record as raising it. `checkerName` is the admin who released or
   * rejected it: null until it is decided, and on the historical rows decided with no checker on
   * record.
   */
  makerName: z.string().nullable(),
  checkerName: z.string().nullable(),
  /**
   * WHERE THE MONEY STANDS ONCE IT IS RELEASED — the payout, which is a separate
   * question from the request's status (contracts/payout-accounts.ts).
   *
   * `sendable` is whether this platform hands the payout to a provider at all: a
   * StarPay rail and a registered payout account. Every other row is recorded at
   * release and sent by a person, and reads NOT_SENT for good. The parked fields
   * say why a payout is waiting for an operator; `nameMatch` and
   * `payoutAccountStatus` are the destination account's, null for a row raised
   * before payout accounts existed.
   */
  payoutState,
  sendable: z.boolean(),
  provider: z.string(),
  parkedAt: z.iso.datetime().nullable(),
  parkedReason: parkedReason.nullable(),
  sendAttempts: z.number().int(),
  queryFailures: z.number().int(),
  providerRef: z.string().nullable(),
  instapayRef: z.string().nullable(),
  sentAt: z.iso.datetime().nullable(),
  paidAt: z.iso.datetime().nullable(),
  retryAfter: z.iso.datetime().nullable(),
  reversedAt: z.iso.datetime().nullable(),
  nameMatch: nameMatch.nullable(),
  payoutAccountStatus: payoutAccountStatus.nullable(),
});
export type WithdrawalRow = z.infer<typeof withdrawalRow>;

/* ── Transactions ────────────────────────────────────────────────────────── */

export const transactionKind = z.enum([
  'DEPOSIT', 'WITHDRAWAL', 'BET', 'WIN',
  /*
   * BONUS_CONVERT is the third bonus movement and it arrived with the grant
   * path in §12. The other two were always in this list and nothing wrote
   * either of them.
   *
   * It has to be its OWN kind rather than borrow one, because it is the only
   * movement in the ledger with both sides on the SAME player: PLAYER_BONUS is
   * debited and PLAYER_CASH is credited when a wagering requirement is met.
   * Filed under ADJUSTMENT it would read as a manual correction somebody made;
   * filed under BONUS_GRANT it would be counted a second time as bonus cost on
   * the Transactions strip and in every "what did we spend on bonuses" answer.
   *
   * The amount a conversion shows on that screen is ZERO, and correctly so: the
   * strip signs entries against the PLAYER's accounts, both of these are the
   * player's, and nothing entered or left their side of the ledger — the money
   * stopped being locked. That is a fact about the row, not a gap in it.
   */
  'BONUS_GRANT', 'BONUS_CONVERT', 'BONUS_FORFEIT', 'ADJUSTMENT', 'COMMISSION', 'REVERSAL',
]);
export const transactionStatus = z.enum([
  'SETTLED', 'PENDING', 'AWAITING_CHECKER', 'FAILED', 'REVERSED',
]);

export const transactionListQuery = pageQuery.extend({
  q: z.string().max(200).optional(),
  brandId: z.string().optional(),
  /** "One ledger … type is a filter, not a separate screen." */
  kind: transactionKind.optional(),
  status: transactionStatus.optional(),
  playerId: z.string().optional(),
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
});

export const transactionRow = z.object({
  id: z.string(),
  ref: z.string(),
  kind: transactionKind,
  status: transactionStatus,
  playerRef: z.string().nullable(),
  playerName: z.string().nullable(),
  brandName: z.string().nullable(),
  /** Signed against the player's balance: deposits and wins credit. */
  amount: centavos,
  providerErrorCode: z.string().nullable(),
  occurredAt: z.iso.datetime(),
});
export type TransactionRow = z.infer<typeof transactionRow>;

/* ── §9 Player funds — the summary band, and the deposit retry record ────── */

/**
 * The scope of the fund summary. One optional brand, the same header control
 * every other §9 read takes; the caller's own brand RESTRICTION is resolved
 * server-side and is never a parameter a client can widen.
 */
export const fundsSummaryQuery = z.object({
  brandId: z.string().optional(),
});
export type FundsSummaryQuery = z.infer<typeof fundsSummaryQuery>;

/**
 * "Released today", "Deposits today" and "Awaiting my approval" — the three
 * figures the Player funds band had no source for.
 *
 * TODAY IS THE GAMING DAY, not the UTC day. `gamingDay()` in
 * packages/db/src/gaming-day.ts draws the boundary once, in Asia/Manila, and
 * every writer on this platform calls it; the window this response reports is
 * the one the queries actually used, so a screen can print the boundary rather
 * than assume it.
 *
 * `averageReleaseMinutes` and `failureRateBp` are NULLABLE, and null means "no
 * denominator" — no payout released today, no deposit attempted today. A zero
 * would be a different claim, and on a payout screen it is the wrong one.
 */
export const fundsSummary = z.object({
  gamingDay: z.object({
    /** Inclusive lower bound, as an instant. Manila midnight is 16:00Z. */
    from: z.iso.datetime(),
    /** The moment the figures were read. The day is still open. */
    asOf: z.iso.datetime(),
    timezone: z.string(),
  }),
  releasedToday: z.object({
    value: centavos,
    payoutCount: z.number().int().nonnegative(),
    /** Request → release, averaged over the payouts released today. */
    averageReleaseMinutes: z.number().int().nonnegative().nullable(),
  }),
  depositsToday: z.object({
    settledValue: centavos,
    settledCount: z.number().int().nonnegative(),
    failedCount: z.number().int().nonnegative(),
    /** failed ÷ (settled + failed), in basis points. Null when neither happened. */
    failureRateBp: z.number().int().nonnegative().nullable(),
  }),
  awaitingMyApproval: z.object({
    /**
     * Open requests across the WHOLE queue in the caller's brand scope whose
     * maker is not the caller — null when the caller cannot be a checker at
     * all, which is a different fact from a workload of zero.
     */
    count: z.number().int().nonnegative().nullable(),
    /** Why the count is null, when it is. */
    unavailable: z.string().nullable(),
    /** The whole open queue, so the screen can say what the exclusion removed. */
    openTotal: z.number().int().nonnegative(),
    raisedByMe: z.number().int().nonnegative(),
  }),
  /**
   * The payout provider, in the same scope as the rest of the band: which driver
   * is on, how many payouts are with it, how many wait for an operator, how many
   * of its notifications wait for a person, whether payouts are stopped, and the
   * float. The float is null when the driver is OFF or StarPay did not say.
   */
  payouts: z.object({
    provider: z.enum(['OFF', 'MOCK', 'STARPAY']),
    inFlight: z.number().int().nonnegative(),
    parked: z.number().int().nonnegative(),
    /**
     * StarPay notifications still waiting for a person (see `payoutProviderStatus`): one
     * that names a payout counts while that payout waits for an operator, within the band's
     * scope; one that names none belongs to no brand, and counts — for a reader with no brand
     * restriction only — until a person marks it seen. The parked-notification list, read with
     * the band's brand, shows exactly this many waiting rows.
     */
    inboxParked: z.number().int().nonnegative(),
    breakerTripped: z.boolean(),
    floatCentavos: centavos.nullable(),
    floatCurrency: z.string().nullable(),
  }),
});
export type FundsSummary = z.infer<typeof fundsSummary>;

/**
 * Why an operator is recording a retry attempt.
 *
 * A closed set for the same reason `withdrawalRejectionReason` is one: the
 * reason drives the count of why deposits are retried, and prose cannot be
 * grouped. Note that none of these says the money moved — see
 * `recordDepositRetryRequest`.
 */
export const depositRetryReason = z.enum([
  'PROVIDER_TIMEOUT',
  'PROVIDER_ADVISED_RETRY',
  'RAIL_RECOVERED',
  'PLAYER_CONFIRMED_DEBIT',
  'MANUAL_RECONCILIATION',
]);
export type DepositRetryReason = z.infer<typeof depositRetryReason>;

/**
 * Recording an operator-initiated retry attempt.
 *
 * This writes a ROW. It does not call a payment provider, because there is no
 * provider integration to call — that is deferred, and the response says so on
 * every success rather than letting a 200 imply a payment was re-submitted.
 * `deposit_retry_not_submitted` on the table refuses any row that claims
 * otherwise.
 *
 * The note is required and at least ten characters, and
 * `deposit_retry_has_reason` refuses a shorter one at the database — so a route
 * that forgot to ask could not write the row either.
 */
export const recordDepositRetryRequest = z.object({
  reasonCode: depositRetryReason,
  note: z
    .string()
    .min(10, 'Say what you are retrying and why — at least 10 characters.')
    .max(500),
});
export type RecordDepositRetryRequest = z.infer<typeof recordDepositRetryRequest>;

export const depositRetryAttemptRow = z.object({
  id: z.string(),
  ref: z.string(),
  transactionId: z.string(),
  transactionRef: z.string(),
  depositId: z.string().nullable(),
  reasonCode: z.string(),
  note: z.string(),
  providerErrorCode: z.string().nullable(),
  outcome: z.string(),
  requestedBy: z.string(),
  requestedByLabel: z.string().nullable(),
  createdAt: z.iso.datetime(),
});
export type DepositRetryAttemptRow = z.infer<typeof depositRetryAttemptRow>;

/**
 * How long the same deposit must wait between two recorded attempts.
 *
 * The rate limit the attempt table makes possible. It is deliberately modest —
 * the point is that a burst of identical attempts cannot be recorded faster
 * than anyone could act on one, not that a retry is rationed.
 */
export const DEPOSIT_RETRY_COOLDOWN_MINUTES = 5;

/**
 * Reading the attempts already recorded.
 *
 * `transactionId` is REPEATED in the query string —
 * `?transactionId=a&transactionId=b` — so the deposits table can ask about the
 * rows it has loaded in one round trip instead of one per row. Node's own
 * query parser hands a repeated key back as an array and a single one as a
 * string, so both shapes are accepted and normalised to a list here rather
 * than in every handler.
 *
 * Capped at 50 ids, which is two pages of the deposits table: a caller cannot
 * turn an enrichment read into an unbounded IN list.
 */
export const depositRetryAttemptQuery = z.object({
  brandId: z.string().optional(),
  transactionId: z.preprocess(
    (v) => (v === undefined ? undefined : Array.isArray(v) ? v : [v]),
    z.array(z.string().min(1)).max(50).optional(),
  ),
  limit: z.coerce.number().int().positive().max(200).default(100),
});
export type DepositRetryAttemptQuery = z.infer<typeof depositRetryAttemptQuery>;

/* ── Agent portal ────────────────────────────────────────────────────────── */

export const agentEarnings = z.object({
  agent: z.object({
    ref: z.string(),
    name: z.string(),
    tier: z.enum(['MASTER', 'SUB_AGENT']),
    brandName: z.string(),
    /** Basis points. 500 is 5.0%. */
    rateBp: z.number().int(),
    status: z.string(),
  }),
  period: z.object({
    label: z.string(),
    from: z.iso.datetime(),
    to: z.iso.datetime(),
  }),
  netRevenue: centavos,
  commission: centavos,
  overrideCommission: centavos,
  unpaidBalance: centavos,
  carryOver: centavos,
  nextPayoutAt: z.iso.datetime().nullable(),
  activePlayers: z.number().int().nonnegative(),
  subAgentCount: z.number().int().nonnegative(),
  pendingKyb: z.number().int().nonnegative(),
  newDeposits: centavos,
  depositCount: z.number().int().nonnegative(),
  /** Commission by day, for the chart. */
  daily: z.array(z.object({ day: z.iso.date(), commission: centavos })),
});
export type AgentEarnings = z.infer<typeof agentEarnings>;

export const subAgentRow = z.object({
  id: z.string(),
  ref: z.string(),
  name: z.string(),
  status: z.string(),
  joinedAt: z.iso.datetime(),
  playerCount: z.number().int().nonnegative(),
  netRevenue: centavos,
  splitBp: z.number().int(),
  theirCut: centavos,
  /** The differential: netRevenue x (own rate - their split). */
  yourCut: centavos,
  /** A suspended sub-agent's commission is held, not paid. */
  held: z.boolean(),
  note: z.string().nullable(),
});
export type SubAgentRow = z.infer<typeof subAgentRow>;

export const adjustSplitRequest = z.object({
  subAgentId: z.string(),
  /** Basis points. Capped at the recruiting agent's own rate. */
  splitBp: z.number().int().min(0).max(10000),
  reason: z.string().min(3, 'Give a reason — the sub-agent sees it.').max(500),
});
export type AdjustSplitRequest = z.infer<typeof adjustSplitRequest>;

export const recruitSubAgentRequest = z.object({
  name: z.string().min(2).max(200),
  phone: z.string().min(7).max(32),
  email: z.email(),
  splitBp: z.number().int().min(0).max(10000),
  area: z.string().max(80),
});

/* ── Affiliate portal ────────────────────────────────────────────────────── */

export const affiliateEarnings = z.object({
  affiliate: z.object({
    ref: z.string(),
    name: z.string(),
    brandName: z.string(),
    status: z.string(),
    model: z.enum(['REVSHARE', 'CPA', 'HYBRID']),
    revshareBp: z.number().int().nullable(),
    cpaAmount: centavos.nullable(),
  }),
  period: z.object({ label: z.string(), from: z.iso.datetime(), to: z.iso.datetime() }),
  netRevenue: centavos,
  commission: centavos,
  unpaidBalance: centavos,
  carryOver: centavos,
  nextPayoutAt: z.iso.datetime().nullable(),
  /** Clicks → signups → first deposits → EPC. */
  funnel: z.object({
    clicks: z.number().int().nonnegative(),
    signups: z.number().int().nonnegative(),
    firstDeposits: z.number().int().nonnegative(),
    /** Earnings per click, in centavos. */
    epc: centavos,
  }),
  referredPlayers: z.number().int().nonnegative(),
  conversionBp: z.number().int().nullable(),
  averageFirstDeposit: centavos,
  daily: z.array(z.object({ day: z.iso.date(), commission: centavos })),
});
export type AffiliateEarnings = z.infer<typeof affiliateEarnings>;

export const trafficSourceRow = z.object({
  linkId: z.string(),
  subId: z.string(),
  channel: z.string(),
  url: z.string(),
  clicks: z.number().int().nonnegative(),
  signups: z.number().int().nonnegative(),
  firstDeposits: z.number().int().nonnegative(),
  conversionBp: z.number().int().nullable(),
  epc: centavos,
  earned: centavos,
  /** EXCELLENT · GOOD · WATCH — below the plan threshold opens a review. */
  quality: z.enum(['EXCELLENT', 'GOOD', 'WATCH']),
});
export type TrafficSourceRow = z.infer<typeof trafficSourceRow>;

export const creativeRow = z.object({
  id: z.string(),
  name: z.string(),
  kind: z.enum(['BANNER', 'SOCIAL', 'STORY', 'TEXT']),
  size: z.string().nullable(),
  languages: z.string(),
  approved: z.boolean(),
});

/* ── Partner-facing, platform-controlled (read-only) ─────────────────────── */

export const lockedSection = z.enum(['WITHDRAWALS', 'TRANSACTIONS', 'GAMES', 'PROMOTIONS']);

export const lockedTable = z.object({
  section: lockedSection,
  title: z.string(),
  subtitle: z.string(),
  note: z.string(),
  columns: z.array(z.string()),
  rows: z.array(z.array(z.string())),
});
export type LockedTable = z.infer<typeof lockedTable>;

/* ── Game catalogue: status changes (§6) ─────────────────────────────────── */

/** LIVE · MAINTENANCE · DISABLED. The whole vocabulary `game.game.status` takes. */
export const gameStatus = z.enum(['LIVE', 'MAINTENANCE', 'DISABLED']);
export type GameStatus = z.infer<typeof gameStatus>;

/**
 * Why a game changed status.
 *
 * A closed set, not free text. The configuration log is what an operator reads
 * six months later to find out why a game went dark, and "how often did a
 * provider incident take a game off the shelf this quarter" is a question prose
 * cannot answer — a report can group a code and cannot group a sentence.
 *
 * Every code reads in BOTH directions, because the same fact ends a window as
 * well as starting one: a game returns to LIVE at the end of a
 * SCHEDULED_MAINTENANCE just as it left LIVE at the start of one.
 */
export const gameStatusChangeReason = z.enum([
  'SCHEDULED_MAINTENANCE',
  'PROVIDER_INCIDENT',
  'COMPLIANCE_HOLD',
  'RTP_DEVIATION',
  'COMMERCIAL_DECISION',
  'CATALOGUE_LAUNCH',
]);
export type GameStatusChangeReason = z.infer<typeof gameStatusChangeReason>;

/**
 * `game.game_status_change` carries ONE `reason` column, not a code and a note.
 *
 * So the two are stored joined by this separator, and split back apart on the
 * way out. The code stays at the front, where `split_part(reason, ' — ', 1)`
 * groups it and an operator reads it first; the note follows, because a code on
 * its own reads as a form number six months later.
 *
 * Shared here rather than written twice: a route that joined with one string
 * and a screen that split on another would silently render every reason as
 * uncategorised prose.
 */
export const GAME_REASON_SEPARATOR = ' — ';

export const changeGameStatusRequest = z.object({
  /** One id is the drawer's single-game action; many is the bulk bar. */
  gameIds: z
    .array(z.string().min(1))
    .min(1, 'Select at least one game.')
    .max(200, 'Change at most 200 games at once.'),
  status: gameStatus,
  reasonCode: gameStatusChangeReason,
  /** Long enough to say something. The note is the half a human actually reads. */
  note: z
    .string()
    .min(10, 'Say what happened — at least 10 characters.')
    .max(500),
});
export type ChangeGameStatusRequest = z.infer<typeof changeGameStatusRequest>;

export const changeGameStatusResult = z.object({
  status: gameStatus,
  count: z.number().int().nonnegative(),
  /** What each game was BEFORE, so the caller can report the transition. */
  changed: z.array(
    z.object({
      id: z.string(),
      code: z.string(),
      name: z.string(),
      fromStatus: gameStatus,
    }),
  ),
});
export type ChangeGameStatusResult = z.infer<typeof changeGameStatusResult>;

/* ── Game catalogue: a PROVIDER's status (§8 of the Game management ACs) ──── */

/**
 * A provider takes the same three states a game does, and the column is the
 * same `varchar(16)`. Reusing `gameStatus` rather than declaring a parallel
 * enum: two vocabularies for one word is how a screen ends up able to send
 * `DISABLED` to one route and `OFF` to the other.
 */
export const providerStatus = gameStatus;
export type ProviderStatus = z.infer<typeof providerStatus>;

/**
 * Why a PROVIDER changed status — a subset of the game reasons, not a new
 * vocabulary.
 *
 * Five of the six read at provider level and keep their meaning, so both logs
 * group on the same codes (`split_part(reason, ' — ', 1)`) and a quarter's
 * "how often did a provider incident take something off the shelf" spans them
 * without a translation table.
 *
 * `RTP_DEVIATION` is the one left out, deliberately: RTP is measured per title
 * against that title's declared figure. A provider has no declared RTP to
 * deviate from, and offering the code here would invite an operator to record a
 * measurement that does not exist.
 */
export const providerStatusChangeReason = z.enum([
  'SCHEDULED_MAINTENANCE',
  'PROVIDER_INCIDENT',
  'COMPLIANCE_HOLD',
  'COMMERCIAL_DECISION',
  'CATALOGUE_LAUNCH',
]);
export type ProviderStatusChangeReason = z.infer<typeof providerStatusChangeReason>;

/**
 * ONE provider per call, and the provider is in the path, not the body.
 *
 * The games route takes a list because the Games tab has a bulk bar behind it.
 * The Providers tab has no such control, and a bulk form here — select five
 * providers and disable them together — is deliberately left for a later pass
 * (`GM-93`) rather than assumed: taking several integrations down at once is a
 * different act from taking one down, and it should be designed as one.
 */
export const changeProviderStatusRequest = z.strictObject({
  status: providerStatus,
  reasonCode: providerStatusChangeReason,
  /** Long enough to say something. The note is the half a human actually reads. */
  note: z.string().min(10, 'Say what happened — at least 10 characters.').max(500),
});
export type ChangeProviderStatusRequest = z.infer<typeof changeProviderStatusRequest>;

export const changeProviderStatusResult = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  status: providerStatus,
  /** What the provider was BEFORE, so the caller can report the transition. */
  fromStatus: providerStatus,
  /**
   * WHAT DID NOT HAPPEN, in a number.
   *
   * Moving a provider out of LIVE does not touch a single game's own status —
   * the rule it feeds is enforced on the way UP (`POST /api/games/status`
   * refuses LIVE behind a provider that is not live) and at launch time, not by
   * cascading downwards. That asymmetry is deliberate and it is invisible, so
   * the response states it: after this move, this many games on this provider's
   * shelf are still marked LIVE. An operator taking SA into maintenance needs
   * to know what is still live rather than assume the shelf went dark.
   */
  liveGamesRemaining: z.number().int().nonnegative(),
  /** The whole shelf, so the count above has a denominator. */
  gamesOnShelf: z.number().int().nonnegative(),
});
export type ChangeProviderStatusResult = z.infer<typeof changeProviderStatusResult>;

/* ── Game catalogue: per-brand availability (§7 of the Game management ACs) ─ */

/**
 * Why a title's availability changed on a brand — a subset of the game
 * reasons, so all three logs group on the same codes.
 *
 * Three of the six survive at this level. The other three
 * (`SCHEDULED_MAINTENANCE`, `PROVIDER_INCIDENT`, `RTP_DEVIATION`) describe
 * something wrong with the TITLE or its PROVIDER, and none of those is true of
 * one brand and false of another — a provider outage does not stop at a brand
 * boundary. Offering them here would invite an operator to record a
 * platform-wide fact as a per-brand decision, and the log exists to answer
 * "why did this title vanish from that brand" six months later.
 */
export const gameBrandChangeReason = z.enum([
  'CATALOGUE_LAUNCH',
  'COMMERCIAL_DECISION',
  'COMPLIANCE_HOLD',
]);
export type GameBrandChangeReason = z.infer<typeof gameBrandChangeReason>;

/**
 * THE WHOLE CHIP SET, not a diff.
 *
 * The drawer draws one chip per brand and the operator taps some of them, so
 * the screen's own state IS the whole set — asking it to compute a diff would
 * put the decision "what changed" in the client, where a stale render becomes
 * a write. The route works out which pairs actually moved and logs only those.
 */
export const setGameBrandsRequest = z.strictObject({
  brands: z
    .array(
      z.strictObject({
        brandId: z.string().min(1).max(64),
        enabled: z.boolean(),
      }),
    )
    .min(1, 'Name at least one brand.')
    .max(50, 'Change at most 50 brands at once.'),
  reasonCode: gameBrandChangeReason,
  /** Long enough to say something. The note is the half a human actually reads. */
  note: z
    .string()
    .min(10, 'Say why this shelf changed — at least 10 characters.')
    .max(500),
});
export type SetGameBrandsRequest = z.infer<typeof setGameBrandsRequest>;

export const setGameBrandsResult = z.object({
  gameId: z.string(),
  code: z.string(),
  name: z.string(),
  /** Only the pairs that MOVED. A re-stated flag is not a change and is not here. */
  changed: z.array(
    z.object({
      brandId: z.string(),
      brand: z.string(),
      /** Null when the pair had no row at all — never considered, not switched off. */
      from: z.boolean().nullable(),
      to: z.boolean(),
    }),
  ),
  /**
   * WHAT THE SHELF LOOKS LIKE NOW — WITHIN THE CALLER'S BRAND SCOPE, which is
   * why the name says so.
   *
   * Counted in the same transaction as the write. For an operator restricted
   * to some brands this counts only those: zero means "on no shelf YOU CAN
   * SEE", never "on no shelf anywhere". The two readings diverge only for
   * restricted operators — the case nobody checks by hand — so the field is
   * named for what it counts rather than left to a comment a screen may not
   * read. A platform-wide figure is deliberately NOT in this response: telling
   * a restricted operator how many other brands carry a title is a disclosure,
   * even though it names none of them. The audit row keeps that figure, under
   * its own name, for whoever holds audit.export.
   *
   * SO: do not render "this is on no shelf anywhere" from this number. Render
   * "none of your brands", which is what it says.
   */
  enabledBrandCountInScope: z.number().int().nonnegative(),
  /** Active brands within the caller's scope, so the count above has a denominator. */
  brandCountInScope: z.number().int().nonnegative(),
});
export type SetGameBrandsResult = z.infer<typeof setGameBrandsResult>;

/* ── The same shelf, applied to MANY titles at once ──────────────────────── */

/**
 * ONE CHIP SET, MANY TITLES.
 *
 * Putting a whole provider's catalogue on a brand meant opening 41 drawers and
 * submitting 41 times, because availability had only ever been a per-title
 * write. That is not a scale a person should be asked to click through, and
 * every future provider import has the same shape.
 *
 * THE BRANDS ARE THE SAME FOR EVERY TITLE NAMED, deliberately. A per-title map
 * would let one submission say different things about different games, which is
 * a bulk edit nobody can check before pressing it — and the screen has no way
 * to draw it either. The bulk bar's question is "put THESE titles on THESE
 * shelves", and this is that question and no more.
 *
 * The cap matches `changeGameStatusRequest`: the same bar selects the same
 * rows, so the two writes behind it refuse at the same size rather than one
 * accepting a selection the other rejects.
 */
export const setGamesBrandsRequest = z.strictObject({
  gameIds: z
    .array(z.string().min(1).max(64))
    .min(1, 'Select at least one game.')
    .max(200, 'Change at most 200 games at once.'),
  brands: z
    .array(
      z.strictObject({
        brandId: z.string().min(1).max(64),
        enabled: z.boolean(),
      }),
    )
    .min(1, 'Name at least one brand.')
    .max(50, 'Change at most 50 brands at once.'),
  reasonCode: gameBrandChangeReason,
  note: z
    .string()
    .min(10, 'Say why this shelf changed — at least 10 characters.')
    .max(500),
});
export type SetGamesBrandsRequest = z.infer<typeof setGamesBrandsRequest>;

export const setGamesBrandsResult = z.object({
  /**
   * How many titles actually MOVED — not how many were named.
   *
   * A selection of forty where thirty already sat on the shelf reports ten, and
   * the screen says ten. Reporting the selection size would tell an operator
   * they had changed forty things when they changed ten, on a write that
   * reaches players.
   */
  gamesChanged: z.number().int().nonnegative(),
  /** Named but already holding every setting sent. Not an error, and not silent. */
  gamesUnchanged: z.number().int().nonnegative(),
  /** Every pair that moved, so the console can say what happened per title. */
  changed: z.array(
    z.object({
      gameId: z.string(),
      code: z.string(),
      name: z.string(),
      brandId: z.string(),
      brand: z.string(),
      /** Null when the pair had no row at all — never considered, not switched off. */
      from: z.boolean().nullable(),
      to: z.boolean(),
    }),
  ),
});
export type SetGamesBrandsResult = z.infer<typeof setGamesBrandsResult>;

/* ── Game catalogue: CREATING rows, and EDITING the fields that are ours ──── */

/**
 * Why a catalogue row was created or edited.
 *
 * The same closed-set discipline as every other reason on this screen, and
 * deliberately a SUBSET of `gameStatusChangeReason` plus one code of its own —
 * so `split_part(reason, ' — ', 1)` groups across all four logs on the
 * Configuration log tab without a translation table.
 *
 * `CATALOGUE_LAUNCH` is what a creation is: a title or an integration added to
 * the catalogue. `CATALOGUE_CORRECTION` is the one code no status log needs —
 * "the name was wrong", "the declared RTP was typed as 9600 and the certificate
 * says 9650". A correction is not an incident and not a commercial decision,
 * and recording it as either would make both counts wrong.
 *
 * `RTP_DEVIATION` is absent for the reason it is absent from the provider
 * reasons: it names a MEASUREMENT against a declared figure. Editing the
 * declared figure itself is a correction to the record, not a deviation from it.
 */
export const catalogueChangeReason = z.enum([
  'CATALOGUE_LAUNCH',
  'CATALOGUE_CORRECTION',
  'COMPLIANCE_HOLD',
  'COMMERCIAL_DECISION',
  'PROVIDER_INCIDENT',
]);
export type CatalogueChangeReason = z.infer<typeof catalogueChangeReason>;

/**
 * The note every catalogue write carries.
 *
 * Declared once and shared by all four requests below, at the same floor the
 * status writes use. A shorter minimum here would mean the log's newest rows are
 * the least readable ones, on a tab whose entire purpose is being readable six
 * months later.
 */
const catalogueNote = z
  .string()
  .min(10, 'Say why — at least 10 characters.')
  .max(500);

/**
 * `game.game_provider.driver` — the word the column may hold.
 *
 * Mirrors `GAME_DRIVER_NAMES` in the API's driver port, which is where the
 * authority actually sits (`createGameDriver` refuses any other name, and the
 * database's own CHECK was dropped by 280_game_launch.sql for a worker fixture).
 * Restated here because a CREATE form has to offer a value, and a form that can
 * send a word the factory refuses is a form that builds an unlaunchable shelf.
 *
 * NOT every member is CREATABLE — see `CREATABLE_PROVIDER_DRIVERS`.
 */
export const gameProviderDriver = z.enum(['MOCK', 'LIVE', 'SA', 'JILI']);
export type GameProviderDriver = z.infer<typeof gameProviderDriver>;

/**
 * THE ONLY DRIVER A FORM MAY CREATE, and the reason is not a policy preference.
 *
 * A driver is CODE. `MOCK` is the one member of the set that needs none beyond
 * what already ships — it opens nothing by construction, which is exactly what
 * a catalogue fixture should do. `SA` additionally needs credentials, an
 * outbound host allowlist and a callback IP allowlist that only a DEPLOYMENT can
 * supply (`SA_DRIVER=SA`), and `LIVE` names no integration at all.
 *
 * So a provider created here is a fixture until an engineer ships its driver,
 * and the route says that in a sentence rather than accepting the word and
 * leaving an operator to discover it when a player taps Play.
 */
export const CREATABLE_PROVIDER_DRIVERS: readonly GameProviderDriver[] = ['MOCK'];

/**
 * ADD PROVIDER.
 *
 * `status` IS NOT HERE, and that is the design rather than an omission. Every
 * provider is born DISABLED — the same state `590_sa_gaming.sql` shipped SA
 * itself in, and the same state the SA host importer gives every table it
 * discovers. Going live is a separate, deliberate act with its own endpoint,
 * its own reason vocabulary and its own log row
 * (`POST /api/games/providers/:id/status`), and it is the only kill switch a
 * live integration has. Folding it into a creation form would mean one careless
 * submit could put an uncertified integration in front of players.
 */
export const createProviderRequest = z.strictObject({
  /**
   * The provider's own code — SA, EVO, JILI. UPPER-CASE and no spaces: it is a
   * key, it appears in URLs and log lines, and `game.game_provider.code` is
   * UNIQUE, so a code differing only in case from an existing one would be
   * accepted by the database and read by a human as the same provider.
   */
  code: z
    .string()
    .trim()
    .min(2, 'A provider code is at least 2 characters.')
    .max(32)
    .regex(/^[A-Z0-9_]+$/, 'Use capitals, digits and underscores only — this code appears in URLs.'),
  name: z.string().trim().min(2, 'Give the provider a name.').max(120),
  driver: gameProviderDriver,
  /**
   * Both optional, both nullable, and the difference matters: absent means "not
   * supplied", and that is stored as NULL, which the Providers tab renders as
   * "Not recorded" rather than as a zero. See the column comments in
   * `690_catalogue_writes.sql`.
   */
  certifiedAt: z.iso.date().nullish(),
  revenueShareBp: z.number().int().min(0).max(10_000).nullish(),
  reasonCode: catalogueChangeReason,
  note: catalogueNote,
});
export type CreateProviderRequest = z.infer<typeof createProviderRequest>;

/**
 * ADD GAME.
 *
 * `status` is absent for the reason it is absent above, and with a second
 * reason on top: `POST /api/games/status` refuses LIVE for any game whose
 * provider is not live, so a creation form offering LIVE would have to
 * re-implement that gate or be able to produce a game the platform will not
 * launch. It produces a DISABLED game, and the operator uses the existing
 * status control — which already states the provider gate on screen.
 */
export const createGameRequest = z.strictObject({
  /**
   * `game.game.code` — the PROVIDER's identifier for the title, and the key the
   * SA host importer upserts on (`SA-<HostID>`). Not free-form prose: it is
   * matched against a third party's catalogue, so it takes the same
   * capitals-and-digits shape a provider code does, plus `-` for the
   * `PREFIX-ID` form every imported row already uses.
   */
  code: z
    .string()
    .trim()
    .min(2, 'A game code is at least 2 characters.')
    .max(48)
    .regex(/^[A-Z0-9_-]+$/, 'Use capitals, digits, hyphens and underscores only.'),
  name: z.string().trim().min(2, 'Give the game a name.').max(160),
  providerId: z.string().min(1, 'Choose the provider this title runs on.').max(64),
  category: gameCategory,
  /**
   * The provider's own type word, lower-case — `bac`, `rot`, `dtx`. Optional
   * because only a live-casino floor has one. Stored as given and labelled
   * through `gameSubCategoryLabel`, which NAMES an unknown code rather than
   * swallowing it into "Other".
   */
  subCategory: z.string().trim().min(1).max(32).nullish(),
  /**
   * Basis points, so 96.5% is 9650 — the same integer discipline as every money
   * figure here, and `game.game.declared_rtp_bp` is a bigint for it.
   *
   * The ceiling is 10000 and NOT higher: a declared RTP above 100% is a game
   * that pays out more than it takes, which is a typo in a certificate reading,
   * and this column feeds the RTP deviation report.
   */
  declaredRtpBp: z.number().int().min(0).max(10_000).nullish(),
  reasonCode: catalogueChangeReason,
  note: catalogueNote,
});
export type CreateGameRequest = z.infer<typeof createGameRequest>;

/**
 * EDIT CONFIGURATION — a game's fields, one PATCH, only what moved.
 *
 * WHAT IS NOT EDITABLE, AND WHY EACH IS REFUSED SOMEWHERE ELSE:
 *   · `code` — it is the key a provider's catalogue is matched on. Changing it
 *     does not rename a title, it ORPHANS the row from the provider's own
 *     identifier and the next importer run creates a duplicate beside it.
 *   · `status` — `POST /api/games/status`, which owns the provider gate.
 *   · brand availability — `PUT /api/games/:id/brands`, which reaches players
 *     immediately and has its own reason vocabulary for that reason.
 *
 * EVERY FIELD IS OPTIONAL AND `.nullish()` WHERE THE COLUMN IS NULLABLE, so
 * "leave this alone" and "clear this" are different requests. A route that
 * could not tell them apart would silently wipe a declared RTP every time an
 * operator edited only the name.
 */
export const updateGameConfigRequest = z.strictObject({
  name: z.string().trim().min(2, 'Give the game a name.').max(160).optional(),
  category: gameCategory.optional(),
  subCategory: z.string().trim().min(1).max(32).nullish(),
  declaredRtpBp: z.number().int().min(0).max(10_000).nullish(),
  reasonCode: catalogueChangeReason,
  note: catalogueNote,
});
export type UpdateGameConfigRequest = z.infer<typeof updateGameConfigRequest>;

/**
 * A GAME'S OWN PICTURE — permission to upload one, asked before any bytes move.
 *
 * What the browser SAYS it will send. Refused here so an operator learns before
 * the upload, but not believed: the confirm route asks the store what arrived
 * and refuses it again from the store's own numbers.
 */
export const gamePictureTicketRequest = z.strictObject({
  contentType: z.enum(GAME_PICTURE_TYPES, {
    error: 'Use a PNG, JPEG or WebP picture. SVG is not accepted because it can carry a script.',
  }),
  sizeBytes: z.number().int().positive().max(MAX_GAME_PICTURE_BYTES, 'That picture is larger than 2 MB.'),
});
export type GamePictureTicketRequest = z.infer<typeof gamePictureTicketRequest>;

/** Where the browser puts the picture, and the headers it must send with it. */
export const gamePictureTicket = z.object({
  key: z.string(),
  url: z.string(),
  headers: z.record(z.string(), z.string()),
  expiresAt: z.string(),
});
export type GamePictureTicket = z.infer<typeof gamePictureTicket>;

/**
 * Make the uploaded picture the one players see, with the reason the
 * configuration log carries — the same reason pair every catalogue write takes.
 */
export const confirmGamePictureRequest = z.strictObject({
  key: z.string().min(1).max(200),
  reasonCode: catalogueChangeReason,
  note: catalogueNote,
});
export type ConfirmGamePictureRequest = z.infer<typeof confirmGamePictureRequest>;

/**
 * Go back to the provider's picture. A POST, not a DELETE: the reason travels in
 * the body, and no route here takes a body on a DELETE.
 */
export const revertGamePictureRequest = z.strictObject({
  reasonCode: catalogueChangeReason,
  note: catalogueNote,
});
export type RevertGamePictureRequest = z.infer<typeof revertGamePictureRequest>;

/**
 * EDIT A PROVIDER'S COMMERCIAL RECORD — the two columns the Providers tab spent
 * its whole life reporting as "missing, not blank".
 *
 * `code`, `name` and `driver` are absent. A provider's code is referenced by
 * every game on its shelf and by the driver factory; its driver is code that
 * either exists or does not. Editing either from a form is a rename that breaks
 * a live integration silently, so neither is offered — a mistyped provider is
 * disabled and replaced, which leaves both acts in the log.
 */
export const updateProviderConfigRequest = z.strictObject({
  certifiedAt: z.iso.date().nullish(),
  revenueShareBp: z.number().int().min(0).max(10_000).nullish(),
  reasonCode: catalogueChangeReason,
  note: catalogueNote,
});
export type UpdateProviderConfigRequest = z.infer<typeof updateProviderConfigRequest>;

/**
 * What a create or an edit reports back.
 *
 * `changed` IS THE HONEST HALF and it is the reason this shape is shared: it
 * lists the fields that actually MOVED, with both ends, so a console can say
 * "name and declared RTP changed" instead of "saved" — and say nothing about a
 * field it did not touch. A create reports every field it set, which is the
 * same statement about the same act.
 */
export const catalogueWriteResult = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  changed: z.array(
    z.object({
      field: z.string(),
      /** Null when the field had no value — never set, as distinct from emptied. */
      from: z.string().nullable(),
      /** Null when the field was CLEARED. */
      to: z.string().nullable(),
    }),
  ),
});
export type CatalogueWriteResult = z.infer<typeof catalogueWriteResult>;

/* ── Manual balance adjustments (§9) ─────────────────────────────────────── */

/**
 * Which way the money goes, from the PLAYER's point of view.
 *
 * `wallet.manual_adjustment.direction` is a varchar(8) with no CHECK behind it,
 * so this enum is the only thing that keeps the column to two values. It is
 * upper-case because that is what the column already holds.
 */
export const adjustmentDirection = z.enum(['CREDIT', 'DEBIT']);
export type AdjustmentDirection = z.infer<typeof adjustmentDirection>;

/**
 * Why a balance was adjusted by hand.
 *
 * A closed set, not free text. The reason code is what a finance report groups
 * by when it answers "how much goodwill did we pay out this quarter, and how
 * much of it was failed-deposit recovery" — a question prose cannot answer.
 * `reason_code` is a free varchar with no reference table, so unlike the
 * withdrawal rejection reason there is no database backstop here: this enum IS
 * the constraint, and that is stated on the screen rather than implied.
 *
 * The two codes the seed already carries — GOODWILL and FAILED_DEPOSIT — are
 * the first two entries deliberately, so the closed set describes the rows that
 * exist rather than orphaning them.
 */
export const adjustmentReasonCode = z.enum([
  'GOODWILL',
  'FAILED_DEPOSIT',
  'ROUND_VOID',
  'BONUS_CORRECTION',
  'CHARGEBACK',
]);
export type AdjustmentReasonCode = z.infer<typeof adjustmentReasonCode>;

/** Why a checker refused one. Separate vocabulary: refusing is not adjusting. */
export const adjustmentRejectionReason = z.enum([
  'INSUFFICIENT_EVIDENCE',
  'WRONG_PLAYER',
  'WRONG_AMOUNT',
  'DUPLICATE_REQUEST',
  'POLICY_BREACH',
  'RAISED_IN_ERROR',
]);
export type AdjustmentRejectionReason = z.infer<typeof adjustmentRejectionReason>;

/** AWAITING_CHECKER until someone decides; then APPROVED or REJECTED, once. */
export const adjustmentStatus = z.enum(['AWAITING_CHECKER', 'APPROVED', 'REJECTED']);
export type AdjustmentStatus = z.infer<typeof adjustmentStatus>;

/**
 * `wallet.manual_adjustment` carries ONE `rejected_reason` column, not a code
 * and a note — the same shape `game.game_status_change` has, and handled the
 * same way. The code stays at the front so `split_part(rejected_reason, ' — ',
 * 1)` groups a refusal report, and the note follows because a code on its own
 * reads as a form number six months later.
 *
 * Shared here rather than written twice: a route that joined with one string
 * and a screen that split on another would render every refusal as
 * uncategorised prose.
 */
export const ADJUSTMENT_REASON_SEPARATOR = ' — ';

/**
 * Centavos, and strictly positive.
 *
 * `manual_adjustment_amount_positive` refuses a zero or negative amount at the
 * database, which is what actually protects the ledger. This refuses it before
 * a row is attempted, so the operator reads the rule rather than a constraint
 * name. The DIRECTION carries the sign; a negative CREDIT is not a debit, it is
 * a mistake.
 */
export const adjustmentAmount = centavos.refine(
  (v) => {
    /*
     * The shape is re-checked here, and not assumed from `centavos`.
     *
     * zod runs EVERY check on a string rather than stopping at the first
     * failure, so this refinement is also handed the values the regex rejected
     * — and `BigInt('2500.75')` does not return false, it THROWS. That escapes
     * safeParse as an ordinary exception and the caller reads a 500 for what is
     * simply a mistyped amount. Guarding here keeps a malformed amount a 400.
     */
    if (!/^-?\d+$/.test(v)) return false;
    return BigInt(v) > 0n;
  },
  { message: 'An adjustment must be a whole number of centavos, greater than zero.' },
);

export const adjustmentQueueQuery = pageQuery.extend({
  brandId: z.string().optional(),
  /** Omitted means the CHECKER QUEUE — the rows still awaiting a decision. */
  status: adjustmentStatus.optional(),
  playerId: z.string().optional(),
});
export type AdjustmentQueueQuery = z.infer<typeof adjustmentQueueQuery>;

export const adjustmentRow = z.object({
  id: z.string(),
  ref: z.string(),
  playerId: z.string(),
  playerRef: z.string(),
  playerName: z.string(),
  brandId: z.string(),
  brandName: z.string(),
  direction: adjustmentDirection,
  amount: centavos,
  /**
   * Read back as a plain string, NOT as `adjustmentReasonCode`.
   *
   * The column is a free varchar with no reference table: a row written before
   * this contract existed, or by a future route that forgets it, can hold
   * anything. Typing the read as the closed set would make the list endpoint
   * throw on data it is supposed to be showing an operator. Writes are closed;
   * reads report what is stored.
   */
  reasonCode: z.string(),
  reasonNote: z.string().nullable(),
  status: adjustmentStatus,
  initiatedBy: z.string(),
  /** Joined to identity.admin_user, so the queue can name the maker. */
  makerLabel: z.string().nullable(),
  approvedBy: z.string().nullable(),
  checkerLabel: z.string().nullable(),
  approvedAt: z.iso.datetime().nullable(),
  rejectedReason: z.string().nullable(),
  transactionId: z.string().nullable(),
  createdAt: z.iso.datetime(),
  /**
   * The player's cash balance AS OF THIS READ, so the queue can show whether a
   * pending debit still fits. It is not a snapshot taken when the request was
   * raised — nothing stores one — and the approval re-checks it under a lock.
   */
  playerCashBalance: centavos,
});
export type AdjustmentRow = z.infer<typeof adjustmentRow>;

export const raiseAdjustmentRequest = z.object({
  playerId: z.string().min(1, 'Choose a player.'),
  direction: adjustmentDirection,
  amount: adjustmentAmount,
  reasonCode: adjustmentReasonCode,
  /* The note is what a second admin reads before deciding, and what an auditor
     reads years later. A code alone reads as a form number. */
  note: z
    .string()
    .min(10, 'Say what happened — at least 10 characters.')
    .max(500),
});
export type RaiseAdjustmentRequest = z.infer<typeof raiseAdjustmentRequest>;

export const approveAdjustmentRequest = z.object({
  /** Optional, but recorded when given — an approval can carry context too. */
  note: z.string().max(500).optional(),
});

export const rejectAdjustmentRequest = z.object({
  reasonCode: adjustmentRejectionReason,
  note: z
    .string()
    .min(10, 'Say why — at least 10 characters. The maker reads this.')
    .max(500),
});
export type RejectAdjustmentRequest = z.infer<typeof rejectAdjustmentRequest>;

/* ── §18 Maintenance windows · §19 Announcements ──────────────────────────
   The platform schedule: what is taken off the floor, and what players are
   told. Both are write paths that change what a PLAYER sees, so both refuse
   with a closed set of reasons rather than free text — a refusal report that
   cannot group its reasons is a list of anecdotes. */

/**
 * The surface a window takes down.
 *
 * A CATEGORY, not a name — the human "Evolution Live casino" is the window's
 * `title`. The overlap rule is scoped by (target, target_id) together, so this
 * value decides which other windows a new one is checked against.
 */
export const maintenanceTarget = z.enum(['PLATFORM', 'PROVIDER', 'PAYMENT_RAIL', 'BRAND']);
export type MaintenanceTarget = z.infer<typeof maintenanceTarget>;

/**
 * How much notice players get, in minutes.
 *
 * A closed set, matching the three leads the design names and the seed writes:
 * 72 hours for a full-downtime release, 24 for a partial window, 12 for a short
 * rail outage. Free minutes would let "5" through, and five minutes' notice of
 * a platform release is not notice.
 */
export const maintenanceNoticeLead = z.union([
  z.literal(720),
  z.literal(1440),
  z.literal(4320),
]);

export const createMaintenanceWindowRequest = z.object({
  target: maintenanceTarget,
  /** Which provider, which rail. Null means the whole category is taken down. */
  targetId: z.string().min(1).max(120).optional(),
  /** Absent means platform-wide: the window is returned in every brand scope. */
  brandId: z.string().min(1).optional(),
  /** The human name the calendar and the table show. */
  title: z.string().min(3).max(200),
  plannedStart: z.iso.datetime(),
  plannedEnd: z.iso.datetime(),
  noticeLeadMinutes: maintenanceNoticeLead,
});
export type CreateMaintenanceWindowRequest = z.infer<typeof createMaintenanceWindowRequest>;

/**
 * Why a planned window was called off.
 *
 * platform.maintenance_window has no cancellation_reason column, so this is
 * carried by the audit row rather than by the window — which is stated on the
 * screen rather than hidden. A code is still a closed set: "why did we call off
 * eleven windows last quarter" is a grouping question, and prose cannot answer
 * it.
 */
export const maintenanceCancelReason = z.enum([
  'NO_LONGER_REQUIRED',
  'RESCHEDULED',
  'CLASHES_WITH_ANOTHER_CHANGE',
  'VENDOR_DEFERRED',
  'NOTICE_TOO_SHORT',
]);
export type MaintenanceCancelReason = z.infer<typeof maintenanceCancelReason>;

export const cancelMaintenanceWindowRequest = z.object({
  reasonCode: maintenanceCancelReason,
  /* Operators read this in the audit trail months later. A code alone reads as
     a form number, so the note is required and long enough to say something. */
  note: z.string().min(10).max(500),
});
export type CancelMaintenanceWindowRequest = z.infer<typeof cancelMaintenanceWindowRequest>;

export const scheduleAnnouncementRequest = z.object({
  /** When the send runs. The audience rule is evaluated then, not now. */
  scheduledFor: z.iso.datetime(),
});
export type ScheduleAnnouncementRequest = z.infer<typeof scheduleAnnouncementRequest>;

/** Why a queued send was called off before it ran. */
export const announcementCancelReason = z.enum([
  'COPY_INCORRECT',
  'WRONG_AUDIENCE',
  'TIMING_CHANGED',
  'CAMPAIGN_WITHDRAWN',
  'REGULATORY_HOLD',
]);
export type AnnouncementCancelReason = z.infer<typeof announcementCancelReason>;

export const cancelAnnouncementSendRequest = z.object({
  reasonCode: announcementCancelReason,
  note: z.string().min(10).max(500),
});
export type CancelAnnouncementSendRequest = z.infer<typeof cancelAnnouncementSendRequest>;

/**
 * Why an automatic player notification was switched off, or back on.
 *
 * ONE set for both directions, not two, because the honest answers run in both:
 * a template is corrected and the notification goes back on under the same code
 * that took it off. The direction is not a matter of opinion — it is the
 * boolean — so splitting the list would only invite a code that contradicts the
 * move it is filed against.
 *
 * `platform.notification_setting` has no reason column, so this rides on
 * `platform.notification_setting_change.reason` and on the audit row. What the
 * closed set buys is the grouping question: "how often did we silence the
 * withdrawal notice last quarter, and on what grounds".
 */
export const notificationSettingReason = z.enum([
  'CHANNEL_OUTAGE',
  'TEMPLATE_INCORRECT',
  'EXCESSIVE_VOLUME',
  'REGULATORY_REQUIREMENT',
  'ISSUE_RESOLVED',
  'COMMERCIAL_DECISION',
]);
export type NotificationSettingReason = z.infer<typeof notificationSettingReason>;

export const setNotificationSettingRequest = z.object({
  /* The state being ASKED FOR, not a toggle. A switch that says "flip it" races
     a second operator on the same row and lands on whichever state nobody
     chose; a switch that says "make it off" is idempotent about the outcome and
     can be refused as a no-op when it already is. */
  enabled: z.boolean(),
  reasonCode: notificationSettingReason,
  note: z
    .string()
    .min(10, 'Say why this notification is changing — at least 10 characters.')
    .max(500),
});
export type SetNotificationSettingRequest = z.infer<typeof setNotificationSettingRequest>;

/* ── Player restrictions and KYC review (§8, §8.4) ────────────────────────
   Appended for the player-actions module. Everything above is untouched. */

/**
 * The five account controls the design draws, as `player_restriction.kind`.
 *
 * SELF_EXCLUSION is in the vocabulary because the column holds it and the
 * profile renders it — but it is NOT in the set staff may apply. A staff-set
 * self-exclusion would be a staff-LIFTABLE one (the database trigger only
 * protects a row whose applied_by is PLAYER), which is a control that reads as
 * a full account lock and is not one. Staff apply the four below; the player's
 * own lock arrives by the player's own hand.
 */
export const restrictionKind = z.enum([
  'WITHDRAWAL_BLOCK',
  'DEPOSIT_LIMIT',
  'BONUS_EXCLUSION',
  'LIVE_RESTRICTED',
  'SELF_EXCLUSION',
]);
export type RestrictionKind = z.infer<typeof restrictionKind>;

/**
 * WHO APPLIED A RESTRICTION — `player.player_restriction.applied_by`.
 *
 * ── WHY THIS EXISTS, WHICH IS FIVE PLACES DISAGREEING ─────────────────────
 *
 * This column had no contract and no CHECK, and five places held an opinion:
 *
 *   the three API writers            PLAYER and STAFF
 *   the reports word map             PLAYER and OPERATOR
 *   the reports test fixture         PLAYER and OPERATOR
 *   the label-completeness test      PLAYER and OPERATOR, hard-coded
 *   the live data                    PLAYER and STAFF
 *
 * So every staff-applied restriction reached a COMPLIANCE REPORT as the raw word
 * STAFF, and the suite stayed green because its fixture wrote a value production
 * never writes. sql/250's own header had already recorded the truth — "Both
 * INSERT sites in the API hardcode applied_by = 'STAFF'" — and the reporting
 * layer never heard.
 *
 * OPERATOR is admitted and must not be written. It is here so a row from before
 * `player_restriction_applied_by_is_known` (sql/1007) still has a word rather
 * than reaching a regulator blank, and so this list can be compared against the
 * constraint rather than against somebody's memory.
 */
export const restrictionSource = z.enum(['PLAYER', 'STAFF', 'OPERATOR']);
export type RestrictionSource = z.infer<typeof restrictionSource>;

/** The four a staff member may apply. */
export const staffRestrictionKind = z.enum([
  'WITHDRAWAL_BLOCK',
  'DEPOSIT_LIMIT',
  'BONUS_EXCLUSION',
  'LIVE_RESTRICTED',
]);
export type StaffRestrictionKind = z.infer<typeof staffRestrictionKind>;

/**
 * Why a control was applied.
 *
 * A closed set for the same reason a withdrawal refusal is one: the code
 * decides what the player is told and is the only thing a compliance report can
 * group. `player_restriction.reason` is a single text column, so the CODE is
 * what is stored there and the operator's note travels with the audit row —
 * prose in the grouped column would make "restrictions opened by fraud review
 * this month" uncountable.
 */
export const restrictionReasonCode = z.enum([
  'RISK_REVIEW',
  'FRAUD_INVESTIGATION',
  'KYC_INCOMPLETE',
  'CHARGEBACK',
  'RESPONSIBLE_GAMING',
  'PLAYER_REQUEST',
  'REGULATORY_ORDER',
]);
export type RestrictionReasonCode = z.infer<typeof restrictionReasonCode>;

/** Why a control was taken off again. Lifting is a decision too. */
export const restrictionLiftReasonCode = z.enum([
  'REVIEW_CLEARED',
  'APPLIED_IN_ERROR',
  'PLAYER_APPEAL_UPHELD',
  'DOCUMENTS_RECEIVED',
  'REGULATORY_ORDER_LIFTED',
]);
export type RestrictionLiftReasonCode = z.infer<typeof restrictionLiftReasonCode>;

/**
 * Apply one control to between one and five hundred players.
 *
 * "Bulk actions cap at 500 rows per submission" was enforced only in the page
 * until this route existed. It is enforced here now, so the number means
 * something on the wire as well as on the screen.
 */
export const applyRestrictionRequest = z
  .object({
    playerIds: z
      .array(z.string().min(1))
      .min(1, 'Select at least one player.')
      .max(500, 'Bulk actions cap at 500 players per submission.'),
    kind: staffRestrictionKind,
    reasonCode: restrictionReasonCode,
    /* A code alone reads as a form number. The note is what the next operator
       reads in the audit trail, so it has to say something. */
    note: z.string().min(10, 'Say what prompted this — at least 10 characters.').max(500),
    /** DEPOSIT_LIMIT only, in centavos. Never a float and never a Number. */
    limitAmount: centavos.optional(),
    /** Open-ended when absent. */
    expiresAt: z.iso.datetime().optional(),
  })
  .superRefine((value, ctx) => {
    // A deposit limit with no amount is not a limit, and an amount on a
    // withdrawal block is a number nothing will ever read.
    if (value.kind === 'DEPOSIT_LIMIT' && value.limitAmount === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['limitAmount'],
        message: 'A deposit limit needs the daily cap it sets.',
      });
    }
    if (value.kind !== 'DEPOSIT_LIMIT' && value.limitAmount !== undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['limitAmount'],
        message: 'Only a deposit limit carries an amount.',
      });
    }
    /*
     * The shape is re-checked here, and not assumed from `centavos`.
     *
     * A field whose regex failed leaves the object DIRTY, not ABORTED, so this
     * refinement is still handed values the regex already rejected — and
     * `BigInt('2500.75')` does not return false, it THROWS. That escapes
     * safeParse as an ordinary exception and the caller reads a 500 for what is
     * simply a mistyped amount. The field's own issue already says the shape is
     * wrong; the sign is only asked of a value that can bear the question.
     */
    if (
      value.limitAmount !== undefined &&
      /^-?\d+$/.test(value.limitAmount) &&
      BigInt(value.limitAmount) <= 0n
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['limitAmount'],
        message: 'A daily cap of zero blocks deposits outright — use a withdrawal block instead.',
      });
    }
  });
export type ApplyRestrictionRequest = z.infer<typeof applyRestrictionRequest>;

export const liftRestrictionRequest = z.object({
  reasonCode: restrictionLiftReasonCode,
  note: z.string().min(10, 'Say why it is coming off — at least 10 characters.').max(500),
});
export type LiftRestrictionRequest = z.infer<typeof liftRestrictionRequest>;

/** One row of player.player_restriction, as the profile reads it. */
export const restrictionRow = z.object({
  id: z.string(),
  kind: restrictionKind,
  /**
   * The stored flag, which is NOT the same question as "is this in force".
   *
   * Nothing clears it when a control reaches its expiry, so an expired row is
   * still active = true. Whether the control binds today is `active` AND an
   * expiry that has not passed — the test the withdrawal release applies.
   */
  active: z.boolean(),
  limitAmount: centavos.nullable(),
  /** PLAYER · STAFF · SYSTEM — this is what decides whether staff may lift it. */
  appliedBy: z.enum(['PLAYER', 'STAFF', 'SYSTEM']),
  appliedByRef: z.string().nullable(),
  /** The reason CODE. The note that came with it is in the audit trail. */
  reason: z.string().nullable(),
  effectiveFrom: z.iso.datetime(),
  expiresAt: z.iso.datetime().nullable(),
  /**
   * The day a lift took the control off. Null while it is on, and null on a row
   * that merely lapsed — an expiry is not a decision, and `expiresAt` is
   * already the day it stopped applying.
   */
  liftedAt: z.iso.datetime().nullable(),
});
export type RestrictionRow = z.infer<typeof restrictionRow>;

/* ── The older KYC submissions — history since 23 October 2026 ───────────
   The owner closed that process: its intake, approve and refusal are gone, and
   their request shapes with them. What stays describes the rows it left, which
   the player's KYC tab lists read-only. */

export const kycSubmissionStatus = z.enum(['PENDING', 'APPROVED', 'REJECTED', 'RESUBMIT']);
export type KycSubmissionStatus = z.infer<typeof kycSubmissionStatus>;

/**
 * Why a submission was refused.
 *
 * Closed, and stored in `kyc_submission.rejection_reason` as the code: it was
 * what the player was shown as the reason to resubmit, and what a monthly
 * "refused for a name mismatch" figure counts. Underage was deliberately absent
 * — it is a permanent account decision, not a document verdict, and offering a
 * resubmit-by date beside it would have invited the player to try again.
 * Nothing refuses a submission now; the codes stay to read the rows that did.
 */
export const kycRejectionReason = z.enum([
  'DOCUMENT_UNREADABLE',
  'DOCUMENT_EXPIRED',
  'NAME_MISMATCH',
  'DATE_OF_BIRTH_MISMATCH',
  'ADDRESS_UNVERIFIED',
  'SELFIE_MISMATCH',
  'SUSPECTED_FORGERY',
  'DUPLICATE_ACCOUNT',
]);
export type KycRejectionReason = z.infer<typeof kycRejectionReason>;

/** One row of player.kyc_submission, as the profile reads it. */
export const kycSubmissionRow = z.object({
  id: z.string(),
  ref: z.string(),
  status: kycSubmissionStatus,
  /** Provider confidence, as stored. Above 0.92 the platform auto-passes. */
  confidence: z.string().nullable(),
  providerRef: z.string().nullable(),
  /** The rejection reason CODE; the note is in the audit trail. */
  rejectionReason: z.string().nullable(),
  resubmitBy: z.iso.datetime().nullable(),
  reviewedBy: z.string().nullable(),
  reviewedByName: z.string().nullable(),
  reviewedAt: z.iso.datetime().nullable(),
  submittedAt: z.iso.datetime(),
});
export type KycSubmissionRow = z.infer<typeof kycSubmissionRow>;

/* ── Points Mall redemption verdicts (§13) ───────────────────────────────── */

/**
 * REQUESTED until someone decides; then APPROVED or REJECTED, once. A merch item
 * then goes to SHIPPED when the desk sends it.
 *
 * SHIPPED IS TERMINAL FOR MERCH, and that is a decision rather than a shortcut.
 * It means "we sent it, on this date, and this admin says so" — a fact the desk
 * knows first-hand. Delivery is not such a fact: nothing here can observe it,
 * because there is no courier integration, no tracking number, no address and no
 * way for a player to confirm receipt. A DELIVERED state would be a claim
 * somebody ticked, and a screen that says a parcel arrived because an operator
 * was asked to say so is worse than one that admits it does not know.
 *
 * FULFILLED has no writer and is not getting one here. The seed writes it on one
 * issued reward; nothing else does. It stays in the vocabulary because a row
 * already carries it, and rewriting historical statuses to tidy an enum is the
 * worse trade — the console names it for what it is instead.
 *
 * `redemption_status_known` in `packages/db/sql/750_redemption_dispatch.sql`
 * closes the same set in the database, which had no CHECK on status at all.
 */
export const redemptionStatus = z.enum([
  'REQUESTED',
  'APPROVED',
  'REJECTED',
  'FULFILLED',
  'SHIPPED',
]);
export type RedemptionStatus = z.infer<typeof redemptionStatus>;

/**
 * Marking an approved merch reward as sent.
 *
 * The note is OPTIONAL, unlike every reason-bearing write on this platform, and
 * the asymmetry is deliberate: a dispatch is not a decision that needs
 * justifying — it is the expected next step on something already approved, and
 * demanding a sentence for it would train the desk to type "sent" ten times a
 * day. What it CAN carry is the only tracking this platform will ever have:
 * there is no carrier field and no address, so "Handed to LBC, receipt 4471" in
 * an operator's own words is the whole of it.
 */
export const shipRedemptionRequest = z.object({
  dispatchNote: z
    .string()
    .trim()
    .max(300, 'Keep the dispatch note under 300 characters.')
    .optional(),
});
export type ShipRedemptionRequest = z.infer<typeof shipRedemptionRequest>;

/**
 * Why a redemption was refused.
 *
 * Closed, and for the same reason the withdrawal and adjustment sets are: the
 * code is what the player is told, and what answers "how many redemptions did
 * we refuse for stock last quarter, and how many for abuse". Prose cannot be
 * grouped, and a refusal report built by reading sentences is not a report.
 *
 * `points.redemption.rejection_reason` is a bare `text` column with no CHECK
 * and no reference table behind it, so — unlike a withdrawal, where
 * `withdrawal_rejection_has_reason` is a real backstop — THIS ENUM IS THE ONLY
 * CONSTRAINT. That is stated on the screen rather than implied.
 */
export const redemptionRejectionReason = z.enum([
  'OUT_OF_STOCK',
  'REWARD_WITHDRAWN',
  'INELIGIBLE_PLAYER',
  'SUSPECTED_ABUSE',
  'DUPLICATE_REQUEST',
  'DELIVERY_UNAVAILABLE',
]);
export type RedemptionRejectionReason = z.infer<typeof redemptionRejectionReason>;

/**
 * `points.redemption` carries ONE `rejection_reason` column rather than a code
 * and a note, the same shape `manual_adjustment` and `game_status_change` have,
 * and it is handled the same way: the code stays at the front so
 * `split_part(rejection_reason, ' — ', 1)` groups a refusal report, and the
 * note follows because a code on its own reads as a form number six months on.
 */
export const REDEMPTION_REASON_SEPARATOR = ' — ';

/**
 * The `reason` a re-credit is written under in `points.points_ledger`.
 *
 * Its own value rather than a positive REDEMPTION row: "points returned by a
 * refused redemption" is a figure a loyalty report asks for directly, and
 * asking it as "REDEMPTION rows whose sign happens to be positive" is a query
 * that reads as a bug. Shared from here so the route that writes it and the
 * screen that describes it cannot drift apart.
 */
export const POINTS_REVERSAL_REASON = 'REDEMPTION_REVERSAL';

/**
 * Approving carries an optional note. Nothing is refused, so nothing has to be
 * explained — but a checker who wants to record why may, and it lands in the
 * audit trail.
 */
export const approveRedemptionRequest = z.object({
  note: z.string().max(500).optional(),
});
export type ApproveRedemptionRequest = z.infer<typeof approveRedemptionRequest>;

/**
 * What a PLAYER sends to redeem a reward.
 *
 * ONE FIELD, and the absence of the others is the security property. There is
 * no `playerId` — identity comes from the session cookie and from nowhere else.
 * There is no `pointsSpent` — the cost is the reward's, read inside the
 * transaction under a lock, because a cost a caller can name is a cost a caller
 * can set to zero. There is no `status` — a player-raised redemption is
 * REQUESTED, and the two verdicts belong to an operator holding
 * `points.verdict`.
 *
 * That leaves a shape with nothing in it worth attacking, which is the point.
 */
export const createRedemptionRequest = z.object({
  rewardId: z.string().min(1).max(64),
});
export type CreateRedemptionRequest = z.infer<typeof createRedemptionRequest>;

export const rejectRedemptionRequest = z.object({
  reasonCode: redemptionRejectionReason,
  note: z
    .string()
    .min(10, 'Say what was wrong — at least 10 characters. The player reads this.')
    .max(500),
});
export type RejectRedemptionRequest = z.infer<typeof rejectRedemptionRequest>;

/* ── Risk rule configuration (§16) ───────────────────────────────────────── */

/**
 * The rule book, as a closed set.
 *
 * `risk_rule.code` is a varchar and the four rows below are what the platform
 * ships. The set is closed here because the THRESHOLD SHAPE is decided by the
 * code — a payout-ratio threshold posted at the deposit-velocity rule is not a
 * different number, it is a different rule — and a shape that cannot be named
 * cannot be validated.
 */
export const riskRuleCode = z.enum([
  'VELOCITY_DEPOSIT',
  'PAYOUT_RATIO',
  'BONUS_ABUSE',
  'SHARED_INSTRUMENT',
]);
export type RiskRuleCode = z.infer<typeof riskRuleCode>;

/**
 * Why a rule was retuned or switched off.
 *
 * Closed, not prose: "we turned the deposit-velocity rule off" is a sentence,
 * and a quarterly review that has to answer "how many controls were disabled
 * for a vendor outage versus retired on purpose" cannot group sentences. The
 * same set serves both writes, so a report reads one column.
 */
export const riskRuleChangeReason = z.enum([
  'THRESHOLD_TUNING',
  'FALSE_POSITIVE_RATE',
  'MISSED_DETECTION',
  'REGULATORY_GUIDANCE',
  'INCIDENT_RESPONSE',
  'RULE_RETIRED',
]);
export type RiskRuleChangeReason = z.infer<typeof riskRuleChangeReason>;

/**
 * "5 deposits in 120 seconds." Both integers, both bounded.
 *
 * `strictObject`, so a payload carrying `ratioBp` alongside — or `deposit`
 * misspelt — is REFUSED rather than merged into the stored jsonb next to the
 * keys the engine actually reads. A config column that silently accumulates
 * keys nobody reads is a threshold nobody can state.
 */
export const velocityDepositThreshold = z.strictObject({
  /** At least two: one deposit is not a velocity. */
  deposits: z.number().int().min(2).max(1000),
  /** Half a minute to a day. A window of zero fires on everything. */
  withinSeconds: z.number().int().min(30).max(86_400),
});
export type VelocityDepositThreshold = z.infer<typeof velocityDepositThreshold>;

/**
 * Payout ratio in BASIS POINTS, so the threshold is an integer.
 *
 * 9000 bp is 90%. Stored as bp for the same reason money is stored in centavos:
 * a float threshold compared against a float ratio is a rule whose edge cases
 * depend on binary rounding.
 */
export const payoutRatioThreshold = z.strictObject({
  ratioBp: z.number().int().min(100).max(50_000),
});
export type PayoutRatioThreshold = z.infer<typeof payoutRatioThreshold>;

/**
 * "Three accounts on one payout instrument inside ninety days."
 *
 * Both integers, both bounded, and the LOWER bound on `accounts` is 2 for the
 * same reason VELOCITY_DEPOSIT's is: one account is not a shared instrument.
 * The upper bound on `withinDays` is two years, which is the AML record-keeping
 * horizon an operator would ever be asked to look back over — not a limit on
 * what the table holds, which nothing deletes, but a limit on how far back a
 * link may reach forward to accuse somebody today.
 */
export const sharedInstrumentThreshold = z.strictObject({
  /** At least two: one account is not a shared instrument. */
  accounts: z.number().int().min(2).max(50),
  withinDays: z.number().int().min(1).max(730),
});
export type SharedInstrumentThreshold = z.infer<typeof sharedInstrumentThreshold>;

/**
 * "Three accounts from one signup origin claiming one promotion inside a week."
 *
 * The same pair of numbers as the instrument rule and a much shorter maximum
 * window, because the two questions age differently. A payout instrument is the
 * same wallet a year later; a signup origin is an address AND a User-Agent
 * string, and a browser update changes the string — so a long window here would
 * not reach further back, it would quietly match less and less while appearing
 * to reach further. Half a year is already generous for that.
 */
export const bonusAbuseThreshold = z.strictObject({
  accounts: z.number().int().min(2).max(50),
  withinDays: z.number().int().min(1).max(180),
});
export type BonusAbuseThreshold = z.infer<typeof bonusAbuseThreshold>;

/**
 * A threshold edit names the rule it is for.
 *
 * The discriminant is the rule CODE, so zod itself refuses a shape that does
 * not belong to the rule; the handler then refuses a code that does not match
 * the row being edited. Two checks, because the first says "this is not a
 * payout-ratio threshold" and the second says "this row is not the payout-ratio
 * rule" — an operator needs to be told which of the two they got wrong.
 *
 * ALL FOUR SHIPPED RULES ARE HERE NOW. BONUS_ABUSE and SHARED_INSTRUMENT were
 * deliberately absent while nothing evaluated them — there was no shape to
 * validate against, and the console left their editor closed rather than
 * storing a jsonb blob no engine reads. `risk.account_link` gave both of them
 * something to measure, `risk.evaluate-rules` measures it, and a rule that is
 * evaluated MUST have an editable threshold: the engine skips a rule whose
 * threshold it cannot read, so a control with no way to set one would be a
 * control that is refused every five minutes.
 *
 * The union is still CLOSED, and that is what it is for: a fifth rule code
 * inserted by hand has no member here, `POST /api/risk/rules/:id/threshold`
 * refuses it, and the console's editor stays shut for it — which is the branch
 * these two rules used to occupy and which is still reachable and still tested.
 */
export const riskRuleThresholdRequest = z.discriminatedUnion('code', [
  z.object({
    code: z.literal('VELOCITY_DEPOSIT'),
    config: velocityDepositThreshold,
    reasonCode: riskRuleChangeReason,
    note: z.string().min(10, 'Say why the threshold is moving — at least 10 characters.').max(500),
  }),
  z.object({
    code: z.literal('PAYOUT_RATIO'),
    config: payoutRatioThreshold,
    reasonCode: riskRuleChangeReason,
    note: z.string().min(10, 'Say why the threshold is moving — at least 10 characters.').max(500),
  }),
  z.object({
    code: z.literal('SHARED_INSTRUMENT'),
    config: sharedInstrumentThreshold,
    reasonCode: riskRuleChangeReason,
    note: z.string().min(10, 'Say why the threshold is moving — at least 10 characters.').max(500),
  }),
  z.object({
    code: z.literal('BONUS_ABUSE'),
    config: bonusAbuseThreshold,
    reasonCode: riskRuleChangeReason,
    note: z.string().min(10, 'Say why the threshold is moving — at least 10 characters.').max(500),
  }),
]);
export type RiskRuleThresholdRequest = z.infer<typeof riskRuleThresholdRequest>;

/** Switching a control on or off. A reason is required in both directions. */
export const riskRuleStateRequest = z.object({
  enabled: z.boolean(),
  reasonCode: riskRuleChangeReason,
  note: z
    .string()
    .min(10, 'Say why the rule is changing state — at least 10 characters.')
    .max(500),
});
export type RiskRuleStateRequest = z.infer<typeof riskRuleStateRequest>;

/* ── Support ticket handling (§15) ───────────────────────────────────────── */

export const ticketStatus = z.enum([
  'OPEN',
  'ANSWERED',
  'PENDING_PLAYER',
  'ESCALATED',
  'RESOLVED',
]);
export type TicketStatus = z.infer<typeof ticketStatus>;

/**
 * The statuses an operator may MOVE a ticket to.
 *
 * ANSWERED is missing on purpose: it is set by posting a public reply and by
 * nothing else, so a control that set it by hand would let a ticket claim the
 * player was answered with no message on the thread — precisely the state the
 * SLA report exists to catch.
 */
export const ticketStatusTarget = z.enum(['OPEN', 'PENDING_PLAYER', 'ESCALATED', 'RESOLVED']);
export type TicketStatusTarget = z.infer<typeof ticketStatusTarget>;

/** Why a ticket moved. Closed, because "why are tickets reopened" is a report. */
export const ticketStatusReason = z.enum([
  'AWAITING_PLAYER_REPLY',
  'NEEDS_SPECIALIST',
  'SUSPECTED_FRAUD',
  'ISSUE_RESOLVED',
  'DUPLICATE_TICKET',
  'PLAYER_CAME_BACK',
]);
export type TicketStatusReason = z.infer<typeof ticketStatusReason>;

export const changeTicketStatusRequest = z.object({
  status: ticketStatusTarget,
  reasonCode: ticketStatusReason,
  note: z.string().min(10, 'Say why it is moving — at least 10 characters.').max(500),
});
export type ChangeTicketStatusRequest = z.infer<typeof changeTicketStatusRequest>;

/** Why a ticket changed hands. The handover is a record, so it has a cause. */
export const ticketReassignReason = z.enum([
  'SPECIALIST_REQUIRED',
  'LANGUAGE_MATCH',
  'WORKLOAD_BALANCE',
  'SHIFT_HANDOVER',
  'CONFLICT_OF_INTEREST',
  'ESCALATION_OWNERSHIP',
]);
export type TicketReassignReason = z.infer<typeof ticketReassignReason>;

export const reassignTicketRequest = z.object({
  /** The admin taking it on. There is no "unassign": a queue with no owner is
      how a ticket goes quiet, so a handover always names someone. */
  toAdminId: z.string().min(1, 'Choose who is taking the ticket.'),
  reasonCode: ticketReassignReason,
  note: z.string().min(10, 'Say why it is changing hands — at least 10 characters.').max(500),
});
export type ReassignTicketRequest = z.infer<typeof reassignTicketRequest>;

/* ── Operator administration (§3) ─────────────────────────────────────────
   The write half of Security & access control.

   Every request below carries a reason CODE from a closed set plus a note.
   The code is what a quarterly access review groups on — "how many role
   changes were offboarding steps?" is a question prose cannot answer — and the
   note is what makes one particular change readable a year later. A code alone
   reads as a form number, which is why the note has a floor rather than being
   optional.

   Role is named rather than identified: the console only ever offers the
   rows in identity.role, the names are unique and system-managed, and the
   matrix endpoint the Role select is populated from returns names and no ids.
   The route resolves the name and refuses an unknown one specifically. */

/** identity.admin_user.status. INVITED has no password and cannot sign in. */
export const adminAccountStatus = z.enum(['INVITED', 'ACTIVE', 'SUSPENDED']);
export type AdminAccountStatus = z.infer<typeof adminAccountStatus>;

/**
 * "The invite expires in 72 hours." The console states it and the route
 * stamps it, so both read the same number from here.
 */
export const INVITE_VALID_HOURS = 72;

export const inviteAdminRequest = z.object({
  email: z.email('Enter a valid work email address.').max(320),
  displayName: z.string().min(2, 'Give their full name as it should appear.').max(200),
  /** identity.role.name — resolved server-side, refused specifically if unknown. */
  roleName: z.string().min(1, 'Choose the role this admin will hold.').max(80),
  /**
   * EMPTY MEANS EVERY BRAND, exactly as identity.admin_brand_scope models it:
   * scope is a restriction, and the absence of rows is the absence of one.
   */
  brandIds: z.array(z.string().min(1)).max(20).default([]),
});
export type InviteAdminRequest = z.infer<typeof inviteAdminRequest>;

/** Why an admin's role moved. Grouped in an access review, so closed. */
export const adminRoleChangeReason = z.enum([
  'PROMOTION',
  'ROLE_REALIGNMENT',
  'TEMPORARY_COVER',
  'SEGREGATION_OF_DUTIES',
  'OFFBOARDING_STEP',
]);
export type AdminRoleChangeReason = z.infer<typeof adminRoleChangeReason>;

export const changeAdminRoleRequest = z.object({
  roleName: z.string().min(1, 'Choose the role they are moving to.').max(80),
  reasonCode: adminRoleChangeReason,
  note: z.string().min(10, 'Say who authorised this — at least 10 characters.').max(500),
});
export type ChangeAdminRoleRequest = z.infer<typeof changeAdminRoleRequest>;

/**
 * Why an account was barred.
 *
 * SUSPECTED_COMPROMISE is deliberately distinct from POLICY_BREACH: one is a
 * security incident that should pull an investigation behind it, the other is
 * an HR matter, and a report that cannot tell them apart is a report nobody
 * can act on.
 */
export const adminSuspensionReason = z.enum([
  'OFFBOARDING',
  'SUSPECTED_COMPROMISE',
  'POLICY_BREACH',
  'INVESTIGATION',
  'EXTENDED_LEAVE',
]);
export type AdminSuspensionReason = z.infer<typeof adminSuspensionReason>;

export const suspendAdminRequest = z.object({
  reasonCode: adminSuspensionReason,
  note: z.string().min(10, 'Say what prompted this — at least 10 characters.').max(500),
});
export type SuspendAdminRequest = z.infer<typeof suspendAdminRequest>;

/** Why the bar came off. SUSPENDED_IN_ERROR is its own code because a mistaken
    suspension is the one outcome an access review must be able to count. */
export const adminReinstatementReason = z.enum([
  'INVESTIGATION_CLEARED',
  'RETURNED_FROM_LEAVE',
  'SUSPENDED_IN_ERROR',
  'ACCESS_RESTORED',
]);
export type AdminReinstatementReason = z.infer<typeof adminReinstatementReason>;

export const reinstateAdminRequest = z.object({
  reasonCode: adminReinstatementReason,
  note: z.string().min(10, 'Say why it is coming off — at least 10 characters.').max(500),
});
export type ReinstateAdminRequest = z.infer<typeof reinstateAdminRequest>;

/**
 * Setting an admin's brand scope AFTER the invite.
 *
 * There was no contract for this, because identity.admin_brand_scope was
 * (admin_id, brand_id) and nothing else: taking a brand off a scope was a
 * DELETE, the application role holds DELETE on no table, and INSERT alone gives
 * a control that can only ever WIDEN a scope — reach granted with no way to
 * take it back, which is the wrong half of a permission control to ship.
 *
 * The table now carries `revoked_at`, so narrowing is a revocation and the
 * control is whole. The request states the scope it wants IN FULL rather than
 * as a diff: "these are the brands this admin may see" is what an operator
 * decides, and a diff would make two concurrent edits silently merge into a
 * scope neither of them chose.
 *
 * An EMPTY list is still the widest scope of all — no rows means every brand —
 * so it is accepted here and refused at the route for an actor who is
 * themselves restricted.
 */
export const adminBrandScopeReason = z.enum([
  'ROLE_REALIGNMENT',
  'BRAND_LAUNCH',
  'BRAND_HANDOVER',
  'ACCESS_REVIEW',
  'SEGREGATION_OF_DUTIES',
  'GRANTED_IN_ERROR',
]);
export type AdminBrandScopeReason = z.infer<typeof adminBrandScopeReason>;

export const setAdminBrandScopeRequest = z.object({
  /** The WHOLE scope after this change. Empty means every brand. */
  brandIds: z.array(z.string().min(1)).max(20),
  reasonCode: adminBrandScopeReason,
  note: z.string().min(10, 'Say why the scope is moving — at least 10 characters.').max(500),
});
export type SetAdminBrandScopeRequest = z.infer<typeof setAdminBrandScopeRequest>;

/**
 * Resetting a second factor.
 *
 * Deliberately distinct codes from a suspension's. A lost phone and a suspected
 * compromise both end in the same write, and an access review that cannot tell
 * them apart cannot tell a helpdesk call from an incident.
 */
export const twoFactorResetReason = z.enum([
  'DEVICE_LOST',
  'DEVICE_REPLACED',
  'SUSPECTED_COMPROMISE',
  'ENROLMENT_ERROR',
  'RETURNING_FROM_LEAVE',
]);
export type TwoFactorResetReason = z.infer<typeof twoFactorResetReason>;

export const resetTwoFactorRequest = z.object({
  reasonCode: twoFactorResetReason,
  note: z.string().min(10, 'Say what prompted the reset — at least 10 characters.').max(500),
});
export type ResetTwoFactorRequest = z.infer<typeof resetTwoFactorRequest>;

/**
 * Ending every session an admin holds, WITHOUT barring the account.
 *
 * Its own codes rather than the suspension's, because it is its own decision.
 * "They left a laptop on a jeepney" and "they are under investigation" produce
 * the same token_version bump and mean opposite things about whether the person
 * should sign back in a minute later — which is the whole reason this is not
 * just a suspension that gets reversed.
 */
export const adminSignOutReason = z.enum([
  'DEVICE_LOST',
  'SUSPECTED_COMPROMISE',
  'SHARED_CREDENTIAL',
  'ROLE_CHANGE_HYGIENE',
  'AT_THEIR_REQUEST',
]);
export type AdminSignOutReason = z.infer<typeof adminSignOutReason>;

export const signOutAdminRequest = z.object({
  reasonCode: adminSignOutReason,
  note: z.string().min(10, 'Say why the sessions are ending — at least 10 characters.').max(500),
});
export type SignOutAdminRequest = z.infer<typeof signOutAdminRequest>;

/**
 * Toggling one cell of the permission matrix.
 *
 * The KEY is validated at the route against PERMISSION_KEYS in @dayon/rbac
 * rather than by an enum here: that constant is the catalogue, and a second
 * copy of it in this file would be one more place for a capability to be
 * missing from. What this schema fixes is the SHAPE — one cell, one direction,
 * and a reason, because "who widened the Risk Officer role and why" is the
 * first question an access review asks.
 */
export const rolePermissionChangeReason = z.enum([
  'ACCESS_REVIEW',
  'ROLE_REALIGNMENT',
  'SEGREGATION_OF_DUTIES',
  'INCIDENT_RESPONSE',
  'GRANTED_IN_ERROR',
  'NEW_RESPONSIBILITY',
]);
export type RolePermissionChangeReason = z.infer<typeof rolePermissionChangeReason>;

export const setRolePermissionRequest = z.object({
  permissionKey: z.string().min(1).max(64),
  /** true grants, false takes it back. Never a toggle of whatever is there:
      two operators clicking the same cell must not race into opposite states. */
  grant: z.boolean(),
  reasonCode: rolePermissionChangeReason,
  note: z.string().min(10, 'Say who authorised this — at least 10 characters.').max(500),
});
export type SetRolePermissionRequest = z.infer<typeof setRolePermissionRequest>;

/* ── The invite link, and accepting it ─────────────────────────────────────
   IT IS BOTH SENT AND SHOWN. `@dayon/mailer` records an ADMIN_INVITE in the
   same transaction as the token; the link is returned to the Super Admin as
   well, because whether that message is DELIVERED is per-environment
   configuration and an environment running MAIL_OUTBOUND_MODE=SUPPRESS delivers
   nothing. The response carries the outbox's own verdict, and every word around
   it reports that rather than claiming a send. */

/** Minimum for a password this platform will store; @dayon/auth refuses less. */
export const ADMIN_PASSWORD_MINIMUM = 12;

export const invitePreviewRequest = z.object({
  token: z.string().min(20, 'That invite link is incomplete.').max(200),
});
export type InvitePreviewRequest = z.infer<typeof invitePreviewRequest>;

export const acceptInviteRequest = z.object({
  token: z.string().min(20, 'That invite link is incomplete.').max(200),
  password: z
    .string()
    .min(
      ADMIN_PASSWORD_MINIMUM,
      `Choose at least ${ADMIN_PASSWORD_MINIMUM} characters — this account can move money.`,
    )
    .max(200),
  /**
   * Six digits from the authenticator, and NOT optional.
   *
   * "2FA must be set up before the first sign-in" is what the invite dialog
   * promises, so acceptance enrols a factor for every role rather than only for
   * the ones that mandate one. It also means a newly accepted admin never sits
   * in the unenrolled state — which is the state a role change into a money
   * role is refused for, and the state a factor reset deliberately creates.
   */
  code: z.string().trim().min(6, 'Enter the 6-digit code from your authenticator app.').max(10),
});
export type AcceptInviteRequest = z.infer<typeof acceptInviteRequest>;

/** Enrolling a factor mid-sign-in, after a reset or a role that now needs one. */
export const enrolTwoFactorRequest = z.object({
  challengeToken: z.string().min(8).max(200),
});
export type EnrolTwoFactorRequest = z.infer<typeof enrolTwoFactorRequest>;

export const confirmTwoFactorEnrolmentRequest = z.object({
  challengeToken: z.string().min(8).max(200),
  code: z.string().trim().min(6).max(10),
});
export type ConfirmTwoFactorEnrolmentRequest = z.infer<typeof confirmTwoFactorEnrolmentRequest>;

/**
 * Exporting the audit trail.
 *
 * A date range and nothing else: the brand scope is taken from the SESSION and
 * never from the request, exactly as every report is, so an export cannot reach
 * a brand its requester cannot open.
 */
export const exportAuditLogRequest = z
  .object({
    from: z.iso.date(),
    to: z.iso.date(),
  })
  .refine((v) => v.from <= v.to, {
    message: 'The range starts after it ends.',
    path: ['from'],
  });
export type ExportAuditLogRequest = z.infer<typeof exportAuditLogRequest>;

/* ── Promotion lifecycle (§12) ─────────────────────────────────────────────
   Appended for the promotions module. Everything above is untouched.

   `promo.promotion` is VERSIONED: (key, version) is the identity, and the
   unique index behind it is what makes "a live promotion is never edited in
   place" a fact rather than a convention. Publishing a CHANGE therefore writes
   a new version and retires the old one; it never rewrites the row a player is
   already holding a bonus against. */

/**
 * The lifecycle vocabulary, read off the column rather than invented.
 *
 * `promo.promotion.status` is a varchar(16) defaulting to 'DRAFT' with NO check
 * constraint, so the database is not a backstop here — the seed carries LIVE,
 * SCHEDULED and ENDED, the screen already renders DRAFT, PAUSED and CANCELLED,
 * and this enum is the only thing that keeps a sixth spelling out of the table.
 */
export const promotionStatus = z.enum([
  'DRAFT',
  'SCHEDULED',
  'LIVE',
  'PAUSED',
  'ENDED',
  'CANCELLED',
]);
export type PromotionStatus = z.infer<typeof promotionStatus>;

/**
 * `promo.promotion.kind`, as the column actually holds it.
 *
 * The design's type list draws five — "Fixed bonus" and "Referral" have no
 * value here, and the seed files both under NO_DEPOSIT. Adding them to this
 * enum would invent a distinction the table cannot record.
 */
export const promotionKind = z.enum(['DEPOSIT_MATCH', 'CASHBACK', 'FREE_SPINS', 'NO_DEPOSIT']);
export type PromotionKind = z.infer<typeof promotionKind>;

/** Auto-applied promotions never ask; opt-in ones do. Lives in the jsonb. */
export const promotionOptIn = z.enum(['AUTO', 'OPT_IN']);
export type PromotionOptIn = z.infer<typeof promotionOptIn>;

/**
 * Wagering, in BASIS POINTS, as an integer string.
 *
 * `wagering_multiplier` is a bigint holding 2000 for a 20× requirement. It
 * crosses the wire as a string for the same reason money does — and it is
 * never a float, because 20.1× rounded by an IEEE double is a different
 * turnover requirement from the one an operator typed.
 */
export const basisPoints = z
  .string()
  .regex(/^\d+$/, 'Expected an integer number of basis points');
export type BasisPointsString = z.infer<typeof basisPoints>;

/**
 * Why a version was published.
 *
 * Closed, for the same reason a withdrawal refusal is: the code is what a
 * "why did the terms change" report groups by, and prose cannot be grouped.
 * The operator's sentence rides in the audit row beside it.
 */
export const promotionPublishReason = z.enum([
  'NEW_CAMPAIGN',
  'TERMS_CORRECTION',
  'WAGERING_CHANGE',
  'BUDGET_CHANGE',
  'WINDOW_CHANGE',
  'REGULATORY_REQUIREMENT',
]);
export type PromotionPublishReason = z.infer<typeof promotionPublishReason>;

/** Why new grants were stopped. Pausing is a decision, so it carries one. */
export const promotionPauseReason = z.enum([
  'BUDGET_EXHAUSTED',
  'ABUSE_SUSPECTED',
  'TERMS_INCORRECT',
  'PROVIDER_OUTAGE',
  'REGULATORY_HOLD',
  'COMMERCIAL_DECISION',
]);
export type PromotionPauseReason = z.infer<typeof promotionPauseReason>;

/** Why they were started again. A resume is a decision too. */
export const promotionResumeReason = z.enum([
  'BUDGET_RESTORED',
  'ABUSE_REVIEW_CLEARED',
  'TERMS_CORRECTED',
  'PROVIDER_RESTORED',
  'REGULATORY_HOLD_LIFTED',
  'COMMERCIAL_DECISION',
]);
export type PromotionResumeReason = z.infer<typeof promotionResumeReason>;

/**
 * Draft a version — of a new campaign, or the next version of an existing one.
 *
 * `wageringMultiplier` is DELIBERATELY optional. A draft is where terms are got
 * right, and the column is nullable, so a draft is allowed to be incomplete;
 * what is not allowed is PUBLISHING one, which the route refuses. Requiring it
 * here would move that rule into the form and out of the platform, where a
 * script writing straight to the API would walk past it.
 */
export const draftPromotionRequest = z
  .object({
    /**
     * Absent means a NEW campaign and the platform mints the key. Present means
     * the next version of that campaign — which is what "Duplicate as draft"
     * sends, and the only way a live promotion's terms are ever changed.
     */
    key: z.string().min(2).max(64).optional(),
    name: z.string().min(3, 'Give the promotion the name players will see.').max(200),
    kind: promotionKind,
    wageringMultiplier: basisPoints.optional(),
    /** Null when there is no cap. Centavos, never a float. */
    maxBonus: centavos.optional(),
    optIn: promotionOptIn,
    /** The human window string the cards read, e.g. "Fri–Sun". jsonb, not a column. */
    window: z.string().max(120).optional(),
    startsAt: z.iso.datetime().optional(),
    endsAt: z.iso.datetime().optional(),
    /** Platform-wide when absent, which is how every seeded promotion is stored. */
    brandId: z.string().min(1).optional(),
    note: z.string().min(10, 'Say what this version is for — at least 10 characters.').max(500),
  })
  .superRefine((value, ctx) => {
    if (value.startsAt && value.endsAt && value.endsAt <= value.startsAt) {
      ctx.addIssue({
        code: 'custom',
        path: ['endsAt'],
        message: 'A promotion cannot close before it opens.',
      });
    }
    /* Re-tested rather than assumed: a value whose regex failed still reaches
       this refinement, and BigInt('20.5') THROWS rather than returning false —
       which escapes safeParse as a 500 for what is simply a mistyped figure. */
    if (
      value.wageringMultiplier !== undefined &&
      /^\d+$/.test(value.wageringMultiplier) &&
      BigInt(value.wageringMultiplier) === 0n
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['wageringMultiplier'],
        message: 'A 0× requirement is no requirement — leave it unset if the bonus is unrestricted.',
      });
    }
    if (
      value.maxBonus !== undefined &&
      /^-?\d+$/.test(value.maxBonus) &&
      BigInt(value.maxBonus) <= 0n
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['maxBonus'],
        message: 'A cap of zero grants nothing. Leave it unset for an uncapped bonus.',
      });
    }
  });
export type DraftPromotionRequest = z.infer<typeof draftPromotionRequest>;

/**
 * Publish a draft.
 *
 * The terms are already on the row — this decides only WHEN it goes live and
 * records WHY. A start still ahead makes it SCHEDULED rather than LIVE, which
 * is the vocabulary the table already carries.
 */
export const publishPromotionRequest = z
  .object({
    startsAt: z.iso.datetime().optional(),
    endsAt: z.iso.datetime().optional(),
    reasonCode: promotionPublishReason,
    note: z.string().min(10, 'Say what is being published — at least 10 characters.').max(500),
  })
  .superRefine((value, ctx) => {
    if (value.startsAt && value.endsAt && value.endsAt <= value.startsAt) {
      ctx.addIssue({
        code: 'custom',
        path: ['endsAt'],
        message: 'A promotion cannot close before it opens.',
      });
    }
  });
export type PublishPromotionRequest = z.infer<typeof publishPromotionRequest>;

/** Stop NEW grants. Grants already held keep running — that is the whole point. */
export const pausePromotionRequest = z.object({
  reasonCode: promotionPauseReason,
  note: z.string().min(10, 'Say why new grants are stopping — at least 10 characters.').max(500),
});
export type PausePromotionRequest = z.infer<typeof pausePromotionRequest>;

/**
 * Why a SCHEDULED promotion was called off before it ever started.
 *
 * ITS OWN VOCABULARY, NOT THE PAUSE ONE, and the difference is the point.
 * Pausing stops a campaign that is RUNNING, so its reasons describe things that
 * went wrong in flight — the budget ran out, abuse showed up, a provider fell
 * over. A cancellation happens to a campaign nobody has been given anything
 * under, so those reasons cannot apply: there is no budget to exhaust and no
 * abuse to find. `SUPERSEDED` and `SCHEDULED_IN_ERROR` are the two this state
 * actually produces, and neither has a home in the pause set.
 *
 * `REGULATORY_HOLD` and `COMMERCIAL_DECISION` are shared with the pause
 * vocabulary deliberately: they mean the same thing in both states, and a
 * second spelling of one reason is how a quarterly report grows a translation
 * table.
 */
export const promotionCancelReason = z.enum([
  'SUPERSEDED',
  'SCHEDULED_IN_ERROR',
  'TERMS_INCORRECT',
  'REGULATORY_HOLD',
  'COMMERCIAL_DECISION',
]);
export type PromotionCancelReason = z.infer<typeof promotionCancelReason>;

/**
 * Call off a schedule that has not started.
 *
 * This is the ONLY write on this module that ends a version's life without it
 * ever having been live, and it is deliberately narrow: only a SCHEDULED
 * promotion can be cancelled. A LIVE one is paused and then allowed to end; a
 * DRAFT one was never promised to anybody and needs no decision recorded.
 */
export const cancelPromotionRequest = z.object({
  reasonCode: promotionCancelReason,
  note: z
    .string()
    .min(10, 'Say why this schedule is being called off — at least 10 characters.')
    .max(500),
});
export type CancelPromotionRequest = z.infer<typeof cancelPromotionRequest>;

/** Start them again. Nothing already expired comes back. */
export const resumePromotionRequest = z.object({
  reasonCode: promotionResumeReason,
  note: z.string().min(10, 'Say why it is starting again — at least 10 characters.').max(500),
});
export type ResumePromotionRequest = z.infer<typeof resumePromotionRequest>;

/* ── §8 Player management: the decisions the module was missing ────────────
   Appended for the player-management module. Everything above is untouched.

   Four writes, and one read that changed shape. Each follows the pattern the
   restriction routes already set: a closed set of reason CODES plus a note that
   has to say something, one transaction carrying both the change and its audit
   row, and — where the roster submits a selection — every player or none of
   them.

   Why a code AND a note, every time: the code is the only thing a compliance or
   commercial report can group ("how many accounts did fraud review suspend last
   quarter"), and prose cannot be grouped. The note is the only thing that makes
   ONE decision legible a year later, and a code on its own reads as a form
   number. Neither substitutes for the other. */

/**
 * An internal note on a player.
 *
 * `player.player_note` is append-only by trigger — no UPDATE and no DELETE path
 * exists from any route, job or psql session — which is what makes a note
 * usable as evidence. The body therefore has a floor: a note that says "ok"
 * costs the same immutability as one that says what happened, and the next
 * operator deciding whether to lift a control is reading this.
 *
 * 2,000 characters rather than the 500 every reason note carries. A note is not
 * a reason for something else; it is the record itself, and a call summary does
 * not fit in a sentence.
 */
export const addPlayerNoteRequest = z.object({
  body: z
    .string()
    .trim()
    .min(10, 'Say what happened — at least 10 characters. This cannot be edited afterwards.')
    .max(2000),
});
export type AddPlayerNoteRequest = z.infer<typeof addPlayerNoteRequest>;

/**
 * Marking a player as an approved test account — PAGCOR item 33(c).
 *
 * THE REASON IS REQUIRED EVEN TO UNMARK. Putting an account back INTO the revenue
 * figures changes a reported number just as surely as taking it out, and "it was
 * marked by mistake" is a sentence an inspection is entitled to read. The floor is
 * the platform's own `AUDIT_REASON_MIN_LENGTH`, shared so the screen and the
 * database cannot hold it to different standards — five contracts once floored a
 * reason at eight while the database floored it at ten, and a nine-character
 * reason came back as "That change is not allowed by the platform rules".
 */
export const markTestAccountRequest = z.object({
  isTest: z.boolean(),
  reason: z
    .string()
    .trim()
    .min(
      AUDIT_REASON_MIN_LENGTH,
      `Say why, in at least ${AUDIT_REASON_MIN_LENGTH} characters. An inspection asks who decided this and why.`,
    )
    .max(500),
});
export type MarkTestAccountRequest = z.infer<typeof markTestAccountRequest>;

/** One row of player.player_note, as the profile reads it. */
export const playerNoteRow = z.object({
  id: z.string(),
  body: z.string(),
  /** The author's display name AS IT WAS when the note was written. */
  authorLabel: z.string(),
  authorId: z.string(),
  createdAt: z.iso.datetime(),
});
export type PlayerNoteRow = z.infer<typeof playerNoteRow>;

/* ── Status: suspend, flag, reinstate ─────────────────────────────────────
   `player.status` carries four values and only one transition was ever written:
   ACTIVE ⇄ RESTRICTED, by the restriction routes. Nothing could suspend an
   account, flag one for watching, or give either back.

   RESTRICTED is deliberately NOT a target here, and a RESTRICTED player is
   deliberately not reinstated from here. That status is owned by
   `player.player_restriction`: reinstating from this route would leave a
   control in force while the roster read Active, which is the exact
   disagreement between the screen and the payout rail that the restriction
   module exists to prevent. The lift route gives the status back, because the
   lift is what makes it true. */

/** The three statuses this route may move a player TO. */
export const playerStatusTarget = z.enum(['SUSPENDED', 'FLAGGED', 'ACTIVE']);
export type PlayerStatusTarget = z.infer<typeof playerStatusTarget>;

/**
 * Which statuses each move may come FROM. This is the whole rule, and it lives
 * here because BOTH sides of it have to agree: the route refuses a move the map
 * does not allow, and the console offers only the moves an account can make.
 * Two copies would eventually offer a button whose only outcome is a refusal —
 * which an operator reads as a broken screen rather than as a rule.
 *
 * SUSPENDED accepts a RESTRICTED player because a suspension is the stronger
 * word. FLAGGED accepts only ACTIVE, because flagging a barred account would
 * WEAKEN it to a watch, and a bar a watch can undo is not a bar. ACTIVE —
 * reinstatement — accepts FLAGGED and SUSPENDED and refuses RESTRICTED: taking
 * a control off is the lift's decision to record, with its own vocabulary and
 * its own audit action.
 */
export const PLAYER_STATUS_TRANSITIONS: Record<PlayerStatusTarget, readonly string[]> = {
  SUSPENDED: ['ACTIVE', 'FLAGGED', 'RESTRICTED'],
  FLAGGED: ['ACTIVE'],
  ACTIVE: ['FLAGGED', 'SUSPENDED'],
};

/**
 * Why an account was barred.
 *
 * Separate from the restriction reasons on purpose. A withdrawal block and a
 * suspension are different acts — one holds money, the other closes the account
 * to play — and a report that cannot tell "we suspended eleven accounts for
 * confirmed fraud" from "we blocked eleven payouts pending review" is a report
 * nobody can act on.
 */
export const playerSuspensionReason = z.enum([
  'FRAUD_CONFIRMED',
  'CHARGEBACK_ABUSE',
  'BONUS_ABUSE',
  'REGULATORY_ORDER',
  'AGE_VERIFICATION_FAILED',
  'DUPLICATE_ACCOUNT',
  'PLAYER_REQUEST',
]);
export type PlayerSuspensionReason = z.infer<typeof playerSuspensionReason>;

/**
 * Why an account was flagged.
 *
 * Flagging is a WATCH, not a control: nothing about a FLAGGED player is blocked
 * anywhere in this platform, and the screen says so rather than implying the
 * flag holds something. What it does is put the account in front of the next
 * reviewer, so the codes are the observations that would put it there.
 */
export const playerFlagReason = z.enum([
  'RISK_REVIEW',
  'UNUSUAL_DEPOSIT_PATTERN',
  'PAYOUT_RATIO',
  'SHARED_INSTRUMENT',
  'SUPPORT_ESCALATION',
  'KYC_MISMATCH',
]);
export type PlayerFlagReason = z.infer<typeof playerFlagReason>;

/**
 * Why a bar or a flag came off.
 *
 * `SUSPENDED_IN_ERROR` and `FLAGGED_IN_ERROR` are separate codes for the same
 * reason the admin-reinstatement set keeps its own: a mistaken suspension is
 * the one outcome a review has to be able to count, and folding it into
 * "cleared" hides exactly the number worth knowing.
 */
export const playerReinstatementReason = z.enum([
  'INVESTIGATION_CLEARED',
  'SUSPENDED_IN_ERROR',
  'FLAGGED_IN_ERROR',
  'DOCUMENTS_RECEIVED',
  'REGULATORY_ORDER_LIFTED',
  'PLAYER_APPEAL_UPHELD',
]);
export type PlayerReinstatementReason = z.infer<typeof playerReinstatementReason>;

/** One to five hundred players, the same cap every bulk submission carries. */
const playerSelection = z
  .array(z.string().min(1))
  .min(1, 'Select at least one player.')
  .max(500, 'Bulk actions cap at 500 players per submission.');

const decisionNote = z
  .string()
  .min(10, 'Say what prompted this — at least 10 characters.')
  .max(500);

/**
 * Move a selection's status.
 *
 * A DISCRIMINATED UNION on the target, so zod itself refuses a suspension
 * carrying a flag's reason code. The alternative — one `reasonCode` covering
 * all three vocabularies — would accept `FLAGGED_IN_ERROR` as a reason to
 * suspend somebody, and the stored code is what a compliance report reads.
 */
export const changePlayerStatusRequest = z.discriminatedUnion('status', [
  z.object({
    playerIds: playerSelection,
    status: z.literal('SUSPENDED'),
    reasonCode: playerSuspensionReason,
    note: decisionNote,
  }),
  z.object({
    playerIds: playerSelection,
    status: z.literal('FLAGGED'),
    reasonCode: playerFlagReason,
    note: decisionNote,
  }),
  z.object({
    playerIds: playerSelection,
    status: z.literal('ACTIVE'),
    reasonCode: playerReinstatementReason,
    note: decisionNote,
  }),
]);
export type ChangePlayerStatusRequest = z.infer<typeof changePlayerStatusRequest>;

/* ── Tier ─────────────────────────────────────────────────────────────────── */

/**
 * Why a tier moved.
 *
 * A tier is a COMMERCIAL decision — it drives the VIP programme and the
 * commission a partner earns on that player — so the codes are commercial ones
 * and the set is closed for the same reason every other is: "how many players
 * did we downgrade for inactivity this quarter" is a grouping question.
 */
export const tierChangeReason = z.enum([
  'VIP_PROGRAMME_REVIEW',
  'LIFETIME_VALUE_THRESHOLD',
  'RETENTION_OFFER',
  'DOWNGRADE_INACTIVITY',
  'RESPONSIBLE_GAMING',
  'APPLIED_IN_ERROR',
  'COMMERCIAL_DECISION',
]);
export type TierChangeReason = z.infer<typeof tierChangeReason>;

export const setPlayerTierRequest = z.object({
  playerIds: playerSelection,
  tier: playerTier,
  reasonCode: tierChangeReason,
  /* A tier change is money: it changes what a player is offered and what an
     agent is paid on them. The note is what a commercial review reads. */
  note: decisionNote,
});
export type SetPlayerTierRequest = z.infer<typeof setPlayerTierRequest>;

/* ── Exporting the roster ─────────────────────────────────────────────────
   A date range and nothing else, exactly as the audit export takes one: the
   BRAND SCOPE COMES FROM THE SESSION and never from the request, so an export
   cannot reach a brand its requester cannot open.

   The window filters `registered_at`, because that is the only date every
   player carries. It is stated on the screen rather than implied — an export
   that quietly dropped everyone who joined before the window would be a roster
   with people missing from it. */
export const exportPlayerRosterRequest = z
  .object({
    from: z.iso.date(),
    to: z.iso.date(),
    /** A brand INSIDE the requester's scope, or absent for all of it. */
    brandId: z.string().min(1).optional(),
  })
  .refine((v) => v.from <= v.to, {
    message: 'The range starts after it ends.',
    path: ['from'],
  });
export type ExportPlayerRosterRequest = z.infer<typeof exportPlayerRosterRequest>;

/* ── §12 Bonus grants — the writes the promotions module never had ─────────
   Appended for the bonus-grant path. Everything above is untouched.

   The promotion lifecycle above decides what a campaign OFFERS. Nothing in it
   ever gave a player anything: `promo.bonus_grant` had exactly one writer in
   this repository — the seed — and the console's own dialog said so. These are
   the shapes behind the four writes that close that:

     POST /api/promotions/:id/grants              promo.grant   an operator grants
     POST /api/promotions/grants/:id/forfeit      promo.grant   an operator takes it back
     POST /api/player/promotions/:id/opt-in       the player themself
     (conversion and expiry are the worker's, and take no request at all)

   Every one of them re-checks the promotion server-side. A client that says a
   promotion is LIVE is not evidence that it is. */

/** `promo.bonus_grant.status`, as sql/270's CHECK constrains it. */
export const bonusGrantStatus = z.enum(['ACTIVE', 'COMPLETED', 'FORFEITED', 'EXPIRED']);
export type BonusGrantStatus = z.infer<typeof bonusGrantStatus>;

/**
 * Where a grant came from.
 *
 * NULL is a third possibility on the column and is deliberately not in this
 * enum: it means "not recorded", which is true of every grant the seed wrote
 * and can never be true of one this platform writes.
 */
export const bonusGrantSource = z.enum(['OPERATOR', 'PLAYER_OPT_IN']);
export type BonusGrantSource = z.infer<typeof bonusGrantSource>;

/**
 * Why an operator granted a bonus by hand.
 *
 * Closed, for the reason every other reason code here is closed: this is what
 * "what did we spend on bonuses, and on what grounds" groups by, and prose
 * cannot be grouped. CAMPAIGN_ENTITLEMENT is the ordinary one — the player
 * qualified and somebody pressed the button; the rest are the cases where a
 * person decided something the campaign did not.
 */
export const bonusGrantReason = z.enum([
  'CAMPAIGN_ENTITLEMENT',
  'SERVICE_RECOVERY',
  'RETENTION_OFFER',
  'VIP_HOST_AWARD',
  'MANUAL_CORRECTION',
]);
export type BonusGrantReason = z.infer<typeof bonusGrantReason>;

/** Why a bonus was taken back. A forfeit is a decision and carries one. */
export const bonusForfeitReason = z.enum([
  'ABUSE_CONFIRMED',
  'TERMS_BREACH',
  'GRANTED_IN_ERROR',
  'PLAYER_REQUEST',
  'ACCOUNT_CLOSED',
]);
export type BonusForfeitReason = z.infer<typeof bonusForfeitReason>;

/**
 * Grant one player one bonus against one published version.
 *
 * The AMOUNT is typed by the operator rather than derived, and that is a
 * statement about what this platform can and cannot compute. A deposit match is
 * a percentage of a deposit, and `promo.promotion` has no percentage column at
 * all — only `max_bonus`, a cap. So there is nothing here to calculate from,
 * and inventing a figure would be worse than asking for one. The route caps
 * what is typed at `max_bonus` where the version has one, which is the only
 * limit the schema can actually enforce.
 *
 * `expiresInDays` is optional and the route's fallback is the PROMOTION's own
 * end. A grant with no expiry at all is legal — `expires_at` is nullable — and
 * the response says so rather than quietly inventing thirty days.
 */
export const grantBonusRequest = z.object({
  playerId: z.string().min(1),
  /** Centavos, as a string. Never a float, never pesos. */
  amount: centavos,
  expiresInDays: z.number().int().min(1).max(365).optional(),
  reasonCode: bonusGrantReason,
  note: z.string().min(10, 'Say why this bonus is being granted — at least 10 characters.').max(500),
}).superRefine((value, ctx) => {
  /* Re-tested rather than assumed, exactly as draftPromotionRequest does it: a
     value whose regex failed still reaches this refinement, and BigInt('5.5')
     THROWS rather than returning false — which escapes safeParse as a 500 for
     what is simply a mistyped figure. */
  if (/^-?\d+$/.test(value.amount) && BigInt(value.amount) <= 0n) {
    ctx.addIssue({
      code: 'custom',
      path: ['amount'],
      message: 'A bonus of nothing is not a bonus. Enter the amount to credit.',
    });
  }
});
export type GrantBonusRequest = z.infer<typeof grantBonusRequest>;

/**
 * Take a bonus back.
 *
 * The money returns to the promotion budget it came from and NOT to the player
 * — the console has said so on the dialog since before there was a route, and
 * this is the route it was waiting for.
 */
export const forfeitBonusRequest = z.object({
  reasonCode: bonusForfeitReason,
  note: z.string().min(10, 'Say why the bonus is being taken back — at least 10 characters.').max(500),
});
export type ForfeitBonusRequest = z.infer<typeof forfeitBonusRequest>;

/* ── Saved views (§ back-office convenience) ───────────────────────────────
   A named filter set an operator can come back to. The stored value is the
   screen's own query string, which is why there is no per-screen shape here —
   `packages/db/sql/720_admin_saved_view.sql` explains the choice. */

/**
 * The screens that offer saved views, matching the CHECK on
 * `platform.admin_saved_view.screen` exactly.
 *
 * A closed set in both places: the database refuses a screen it does not know,
 * and this refuses it before the round trip so the operator reads a sentence
 * rather than a constraint violation.
 */
export const savedViewScreen = z.enum(['TRANSACTIONS', 'WITHDRAWALS', 'PLAYERS', 'GAMES']);
export type SavedViewScreen = z.infer<typeof savedViewScreen>;

/**
 * The query string a view restores, without the leading '?'.
 *
 * Validated by SHAPE and not by content, deliberately. Every screen already
 * validates what it finds in the URL — an unknown status falls back to ALL, a
 * bad page to 1 — so a view can do no more than a person typing in the address
 * bar, and enumerating each screen's parameters here would be a second parser
 * to keep in step with the first.
 *
 * What IS refused is anything that is not a query string: a leading '?' (which
 * would be stored and come back doubled), a fragment, and the control
 * characters that have no business in one.
 */
export const savedViewQuery = z
  .string()
  .max(2048, 'That view carries more filters than this platform stores.')
  .refine((v) => !v.startsWith('?'), {
    message: 'Leave off the leading question mark — store the filters only.',
  })
  .refine((v) => !v.includes('#'), { message: 'A view stores filters, not a page anchor.' })
  // eslint-disable-next-line no-control-regex
  .refine((v) => !/[\u0000-\u001f\u007f]/.test(v), {
    message: 'That view contains characters a web address cannot carry.',
  });

export const saveViewRequest = z.object({
  screen: savedViewScreen,
  /* Trimmed, because " Chargebacks" and "Chargebacks" are one view to the person
     who typed them and two rows to a unique index. */
  name: z
    .string()
    .trim()
    .min(1, 'Give the view a name you will recognise later.')
    .max(60, 'Keep the name under 60 characters.'),
  query: savedViewQuery,
  /** Visible to every admin who can open that screen, and read-only to them. */
  shared: z.boolean().default(false),
});
export type SaveViewRequest = z.infer<typeof saveViewRequest>;

/* ── Adding a reward to the Points catalogue ───────────────────────────────
   `points.reward` had no writer: the console drew "Add reward" hard-disabled
   and said the catalogue was read-only. `packages/db/sql/740_points_reward_
   writes.sql` is the schema half of closing that, including the three CHECKs
   the platform already behaved as if it had. */

/**
 * The three kinds of reward, matching `reward_kind_known` exactly.
 *
 * NOT a free string, and the reason is in the approve path: `verdicts.ts`
 * branches on this value to decide whether a unit of stock is taken. A fourth
 * kind falls through every branch — no stock taken, no cash recorded, a reward
 * that redeems into nothing.
 */
export const rewardKind = z.enum(['CASH', 'BONUS', 'MERCH']);
export type RewardKind = z.infer<typeof rewardKind>;

/** Why a reward was added to the catalogue. Closed, so a change log can be counted. */
export const rewardChangeReason = z.enum([
  'NEW_CAMPAIGN',
  'SEASONAL_OFFER',
  'PARTNER_PROMOTION',
  'REPLACING_WITHDRAWN_REWARD',
  'COMMERCIAL_DECISION',
]);
export type RewardChangeReason = z.infer<typeof rewardChangeReason>;

/**
 * A new catalogue entry.
 *
 * THE SHAPE FOLLOWS THE KIND, and it is refused here as well as by
 * `reward_shape_matches_kind`:
 *
 *   MERCH is shipped → `stock` is required, `cashValue` is refused.
 *   CASH and BONUS are issued → `cashValue` is required, `stock` is refused.
 *
 * Both directions matter and neither is pedantry. `stock IS NULL` means
 * "unlimited, not unknown" to the approve path, so a stock figure on a CASH
 * reward is a limit an operator believes is being enforced and nothing
 * decrements. A cash value on a MERCH reward is a face value nothing will ever
 * pay, sitting in the column a finance report adds up.
 *
 * A zero stock is ALLOWED, and deliberately: "in the catalogue, currently
 * unavailable" is a real state — the seed ships a voucher at zero — and the
 * redemption route already refuses a request against it by name.
 */
export const createRewardRequest = z
  .object({
    name: z
      .string()
      .trim()
      .min(3, 'Give the reward a name a player will recognise.')
      .max(160, 'Keep the name under 160 characters.'),
    kind: rewardKind,
    /** Points, not centavos. A reward costing nothing is not a reward. */
    pointsCost: z
      .string()
      .regex(/^[1-9][0-9]{0,15}$/, 'Enter the points cost as a whole number above zero.'),
    /** Centavos, like every other money figure on this platform. */
    cashValue: z
      .string()
      .regex(/^[1-9][0-9]{0,15}$/, 'Enter the cash value in centavos, above zero.')
      .optional(),
    /** Units on hand. Zero is legal — see above. */
    stock: z.number().int().min(0, 'Stock cannot be negative.').max(1_000_000).optional(),
    /** Live in the player's catalogue immediately, or held back. */
    active: z.boolean().default(true),
    /**
     * The brand this reward is offered to, or ABSENT for every brand.
     *
     * Absent is the default and the historical behaviour: every reward that
     * existed before `790_points_brand_scope.sql` is platform-wide, which is the
     * same thing a null `brand_id` means on a promotion. A brand named here must
     * be one the caller can see — refused rather than silently widened, because
     * an operator who picked one brand and got three would have no way to tell.
     */
    brandId: z.string().min(1).optional(),
    reasonCode: rewardChangeReason,
    note: z
      .string()
      .min(10, 'Say why this reward is being added — at least 10 characters.')
      .max(500),
  })
  .superRefine((value, ctx) => {
    if (value.kind === 'MERCH') {
      if (value.stock === undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['stock'],
          message: 'A merch reward is shipped, so it needs a number of units on hand.',
        });
      }
      if (value.cashValue !== undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['cashValue'],
          message:
            'A merch reward has no cash value — nothing pays one out, and a figure here would be counted as money by a finance report.',
        });
      }
      return;
    }
    if (value.cashValue === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['cashValue'],
        message: `A ${value.kind.toLowerCase()} reward is issued as money, so it needs a cash value.`,
      });
    }
    if (value.stock !== undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['stock'],
        message:
          'Only merch carries stock. A figure here would read as a limit, and nothing would ever decrement it.',
      });
    }
  });
export type CreateRewardRequest = z.infer<typeof createRewardRequest>;

/* ── Taking the maker half of a payout ─────────────────────────────────────
   `payment.withdrawal` has no AWAITING_MAKER status; the design's two
   pre-release stages are split on `initiated_by`, which is what the column
   means — a row with no initiator has had no maker act on it. This is the write
   that puts one there. */

/**
 * Recording that a maker has reviewed one or more player-raised payouts.
 *
 * ── THE CONSEQUENCE THAT SHAPES THIS WHOLE REQUEST ─────────────────────────
 *
 * `withdrawal_maker_is_not_checker` means that once you are a payout's maker you
 * CAN NEVER RELEASE IT. So this write does not advance a request towards being
 * paid — it removes the caller's own ability to pay it, and hands that to
 * somebody else.
 *
 * That makes it the one bulk action on this platform whose danger grows with the
 * selection: an operator who takes fifty rows as maker has just made fifty
 * payouts un-releasable by themselves. If they are the only admin on duty
 * holding `funds.approve`, every one of those players waits until a second admin
 * signs in. The route answers with how many other admins can release, so the
 * console can say it rather than let it be discovered by a player.
 *
 * ── WHY A NOTE AND NOT A REASON CODE ───────────────────────────────────────
 *
 * Every other lifecycle write here carries a code from a closed set, because the
 * question "why did this happen" has countable answers. This one does not: the
 * why is always "I reviewed it". What an auditor actually asks is WHAT WAS
 * CHECKED, which is prose by nature. So the note is required and the code is
 * absent, rather than a one-value enum pretending to be a vocabulary.
 */
export const recordWithdrawalMakerRequest = z.object({
  withdrawalIds: z
    .array(z.string().min(1))
    .min(1, 'Select at least one request.')
    /* Lower than the 500 the roster allows, and deliberately. Each of these is a
       payout the caller is giving up the right to release, and a cap an operator
       can still picture is part of the protection. It is also the queue page. */
    .max(100, 'Take at most 100 requests as maker in one submission.'),
  note: z
    .string()
    .min(10, 'Say what you checked — at least 10 characters.')
    .max(500),
});
export type RecordWithdrawalMakerRequest = z.infer<typeof recordWithdrawalMakerRequest>;

/* ── Exporting the transaction ledger ──────────────────────────────────────
   The Transactions console has drawn a "Queue export" dialog since it shipped,
   with the primary control disabled: "There is no export job endpoint or
   table". That was true of a JOB — and the report engine it needed had been
   running the whole time, storing its artefact and serving a download. This is
   the request that reaches it. */

/**
 * A transaction extract over a window, with the filters the screen was showing.
 *
 * ── THE FILTERS ARE A CLOSED SET, AND NOT THE WHOLE SCREEN ─────────────────
 *
 * `kind`, `status` and `playerId` are carried because `transaction_extract`
 * declares those three columns as filterable. The screen's free-text SEARCH box
 * is NOT carried: matching a reference or a player across two columns is a LIKE
 * rather than an equality, and the report engine's filter map is equality-only
 * by design — the property that lets a filter's value be a bound parameter and
 * never syntax.
 *
 * That gap is stated on the form rather than papered over, because an operator
 * who searched for one player and exported "the current view" would otherwise
 * get a file with every player in it and no way to tell.
 */
export const exportTransactionsRequest = z
  .object({
    from: z.iso.date(),
    to: z.iso.date(),
    /** A brand INSIDE the requester's scope, or absent for all of it. */
    brandId: z.string().min(1).optional(),
    /** Must be one of `transactionKind`; refused by the engine if it is not. */
    kind: transactionKind.optional(),
    status: transactionStatus.optional(),
    playerId: z.string().min(1).optional(),
  })
  .refine((v) => v.from <= v.to, {
    message: 'The range starts after it ends.',
    path: ['from'],
  });
export type ExportTransactionsRequest = z.infer<typeof exportTransactionsRequest>;

/**
 * A risk-alert extract over a window.
 *
 * TWO OF THE FOUR THINGS THE DIALOG PROMISED ARE NOT IN THIS FILE, and cannot
 * be: nothing counts rule FIRES — a rule that evaluated a thousand times and
 * raised two alerts is indistinguishable from one that evaluated twice — and no
 * column anywhere records CONFIRMED FRAUD, because closing an alert carries a
 * note rather than a verdict from a vocabulary. The alerts and how they were
 * answered are real and are here; the form says the other two are absent rather
 * than shipping a header with nothing under it.
 */
export const exportRiskAlertsRequest = z
  .object({
    from: z.iso.date(),
    to: z.iso.date(),
    brandId: z.string().min(1).optional(),
    severity: z.enum(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']).optional(),
    status: z.enum(['OPEN', 'CLOSED']).optional(),
  })
  .refine((v) => v.from <= v.to, {
    message: 'The range starts after it ends.',
    path: ['from'],
  });
export type ExportRiskAlertsRequest = z.infer<typeof exportRiskAlertsRequest>;

/**
 * Which columns one admin has turned off on one screen.
 *
 * ── THE HIDDEN SET, NOT THE VISIBLE ONE ────────────────────────────────────
 *
 * `packages/db/sql/770_admin_column_choice.sql` carries the argument: storing
 * what is VISIBLE freezes a screen at the moment somebody last opened this
 * dialog, so a column added next quarter never appears for anybody who ever
 * saved a choice. Storing what is HIDDEN means a new column shows up by
 * default, and only somebody who goes and hides it misses it.
 *
 * THE KEYS ARE NOT VALIDATED, and that is deliberate. The columns are declared
 * in the console, not on the server, so the route has no list to check against.
 * A key naming no column is stored and hides nothing — harmless, and cheaper
 * than a round trip to be told so. What IS bounded is how many there can be.
 */
export const setColumnChoiceRequest = z.object({
  screen: savedViewScreen,
  hidden: z
    .array(z.string().min(1).max(64))
    .max(64, 'No screen on this platform has that many columns.'),
});
export type SetColumnChoiceRequest = z.infer<typeof setColumnChoiceRequest>;

/* ── Points adjustments (§13) ──────────────────────────────────────────────
   The Points console has drawn a "Points adjustment" button disabled since it
   shipped, saying the maker-checker gate it promises could not be applied
   because `points_ledger` has no status or approver columns. The diagnosis was
   right; the fix is not to put them on a ledger — a ledger row is a movement
   that HAPPENED — but to give the proposal its own table, which is what the
   money side already does. `packages/db/sql/780_points_adjustment.sql` argues
   it in full. */

/**
 * Why points are being moved by hand.
 *
 * Closed, because "how many adjustments did we make for a service failure last
 * quarter, and how many to correct our own arithmetic" is a grouping question
 * and prose cannot answer it. `SYSTEM_ERROR` is deliberately its own code
 * rather than folded into a correction: points awarded by a bug we wrote are a
 * different conversation from points miscounted by a rule working as intended.
 */
export const pointsAdjustmentReason = z.enum([
  'SERVICE_RECOVERY',
  'PROMOTION_CORRECTION',
  'SYSTEM_ERROR',
  'MANUAL_AWARD',
  'CLAWBACK_ABUSE',
  'BALANCE_CORRECTION',
]);
export type PointsAdjustmentReason = z.infer<typeof pointsAdjustmentReason>;

/**
 * PROPOSING an adjustment. It moves nothing — a second admin has to sign it.
 *
 * The direction is explicit rather than a signed number: an amount is always
 * positive, so a report cannot add a debit to a credit and a typed minus cannot
 * turn a give into a take.
 */
export const raisePointsAdjustmentRequest = z.object({
  playerId: z.string().min(1),
  direction: z.enum(['CREDIT', 'DEBIT']),
  /* Whole points, as a string like every other BIGINT on this wire. */
  points: z
    .string()
    .regex(/^[1-9][0-9]{0,15}$/, 'Enter the number of points as a whole number above zero.'),
  reasonCode: pointsAdjustmentReason,
  note: z
    .string()
    .min(10, 'Say what happened — at least 10 characters.')
    .max(500),
});
export type RaisePointsAdjustmentRequest = z.infer<typeof raisePointsAdjustmentRequest>;

/** Approving carries an optional note; nothing is refused, so nothing must be explained. */
export const approvePointsAdjustmentRequest = z.object({
  note: z.string().trim().max(500).optional(),
});
export type ApprovePointsAdjustmentRequest = z.infer<typeof approvePointsAdjustmentRequest>;

/** Refusing carries a required one: the proposer is owed a reason. */
export const rejectPointsAdjustmentRequest = z.object({
  note: z
    .string()
    .min(10, 'Say why it is refused — at least 10 characters.')
    .max(500),
});
export type RejectPointsAdjustmentRequest = z.infer<typeof rejectPointsAdjustmentRequest>;

/**
 * Moving a rail's payout fee between the player and the operator.
 *
 * `bearer` is a closed pair rather than a boolean: `playerPays: false` reads as
 * "not the player" and leaves who DOES pay to the reader, which is exactly the
 * ambiguity the column was added to end.
 */
export const setRailFeeBearerRequest = z.object({
  bearer: z.enum(['PLAYER', 'OPERATOR']),
  reason: z
    .string()
    .min(10, 'Say why this is changing — at least 10 characters.')
    .max(500),
});
export type SetRailFeeBearerRequest = z.infer<typeof setRailFeeBearerRequest>;

/**
 * Moving a payment rail's status.
 *
 * All three are offered because the operator is the only writer: nothing in
 * this platform sets DEGRADED, so leaving it out would make it a state that
 * exists in the vocabulary, is shown to players, and can never be reached.
 */
/**
 * A PAYMENT RAIL'S KIND, AND THE TWO A LICENCE CONDITION BARS.
 *
 * PAGCOR form OMD/Online-0060 Rev. 2, item 17: "Payment gateway solutions are
 * not allowed to utilize credit cards and crypto-currency in account funding
 * and/or withdrawal."
 *
 * CARD and CRYPTO are in the vocabulary rather than struck from it, because the
 * platform ran a live `Visa / Mastercard` rail and the deposits and payouts
 * made over it still point at that row. History has to be able to say the word.
 * What it may not do is offer one: `payment_method_kind_permitted` (sql/900)
 * allows a CARD or CRYPTO row only with the status DISABLED and both direction
 * flags false, and `payment.payment_method.supports_deposit` defaults to true —
 * so creating a usable one is refused rather than quietly allowed.
 *
 * Declared here and not only in SQL so the console can explain the refusal in a
 * sentence instead of showing somebody a constraint name.
 */
export const paymentMethodKind = z.enum(['EWALLET', 'BANK_TRANSFER', 'CARD', 'CRYPTO']);
export type PaymentMethodKind = z.infer<typeof paymentMethodKind>;

/** The kinds that may exist only as history. Nothing may offer one to a player. */
export const RETIRED_RAIL_KINDS: ReadonlySet<string> = new Set(['CARD', 'CRYPTO']);

/**
 * CONDUCTING A HASH CHECK OF THE DEPLOYED FILES.
 *
 * PAGCOR form OMD/Online-0060 Rev. 2, item 44 (NEW ENTRY in Revision 2): the
 * hash value must be given to EGLD and CMED before live commercial operation,
 * and a check "may be requested at any time" by either.
 *
 * `reason` is a closed set because the four cases have different paperwork
 * behind them, and a free-text purpose would make the record unsearchable at
 * the moment somebody needs to prove a specific check happened:
 *
 *   PRE_GO_LIVE        the one item 44 names explicitly
 *   REGULATOR_REQUEST   EGLD or CMED asked -- `note` is then required, below
 *   PERIODIC            a check CMED scheduled
 *   RELEASE             the platform's own, after a deploy
 *
 * A REGULATOR_REQUEST MUST SAY WHO ASKED. This is the one row somebody will be
 * asked to produce the correspondence for, so it may not be recorded without a
 * reference -- the DB states the same rule in `hash_check_request_is_attributed`,
 * and this refuses first so the operator reads a sentence rather than a
 * constraint name.
 */
export const conductHashCheckRequest = z
  .object({
    reason: z.enum(['PRE_GO_LIVE', 'REGULATOR_REQUEST', 'PERIODIC', 'RELEASE']),
    note: z.string().max(500).optional(),
  })
  .refine((v) => v.reason !== 'REGULATOR_REQUEST' || (v.note?.trim().length ?? 0) >= 10, {
    path: ['note'],
    message: 'Say who requested this check and under what reference — at least 10 characters.',
  });
export type ConductHashCheckRequest = z.infer<typeof conductHashCheckRequest>;

export const setRailStatusRequest = z.object({
  status: z.enum(['LIVE', 'DEGRADED', 'DISABLED']),
  reason: z
    .string()
    .min(10, 'Say why this rail is moving — at least 10 characters.')
    .max(500),
});
export type SetRailStatusRequest = z.infer<typeof setRailStatusRequest>;

/**
 * Whether one brand offers a rail to its players: one entry of the Payments
 * screen's read, per rail, for every active brand in the reader's scope. A
 * brand with no row of its own reads as not offered.
 */
export const paymentRailBrand = z.object({
  brandId: z.string(),
  brandName: z.string(),
  enabled: z.boolean(),
});
export type PaymentRailBrand = z.infer<typeof paymentRailBrand>;

/**
 * Offering a rail to one brand's players, or taking it away. It reaches players
 * at their next request — the deposit and withdraw screens list only the rails
 * their brand offers — so it carries a reason, as a status change does.
 */
export const setRailBrandRequest = z.object({
  enabled: z.boolean(),
  reason: z
    .string()
    .trim()
    .min(10, 'Say why this brand’s players gain or lose this rail — at least 10 characters.')
    .max(500),
});
export type SetRailBrandRequest = z.infer<typeof setRailBrandRequest>;

/** ₱100.00: the lowest maximum a rail may be given. A maximum below it would leave the rail all but unusable. */
export const RAIL_MAXIMUM_FLOOR_CENTAVOS = 10_000n;

/** A whole number of centavos above zero, as every amount crosses the wire. */
const railAmount = z
  .string()
  .regex(/^[1-9][0-9]{0,17}$/, 'Enter a whole number of centavos above zero.');

/**
 * A rail's limits for each deposit or withdrawal made over it, in centavos. The
 * maximum is the one an operator lowers when StarPay refuses an amount over its
 * own limit (AM02, AM13). Both are refused here, beside their field, before the
 * database's own rules (`payment_method_range`, a maximum strictly above the
 * minimum) would refuse them in words no screen should print.
 */
export const setRailLimitsRequest = z
  .object({
    minAmount: railAmount,
    maxAmount: railAmount,
    reason: z
      .string()
      .trim()
      .min(10, 'Say why the limits are changing — at least 10 characters.')
      .max(500),
  })
  .refine(
    (v) => !/^\d+$/.test(v.maxAmount) || BigInt(v.maxAmount) >= RAIL_MAXIMUM_FLOOR_CENTAVOS,
    { path: ['maxAmount'], message: 'Set the maximum at ₱100.00 or above.' },
  )
  .refine(
    (v) =>
      !/^\d+$/.test(v.minAmount) ||
      !/^\d+$/.test(v.maxAmount) ||
      BigInt(v.minAmount) < BigInt(v.maxAmount),
    { path: ['maxAmount'], message: 'The maximum must be above the minimum.' },
  );
export type SetRailLimitsRequest = z.infer<typeof setRailLimitsRequest>;

/**
 * Taking a reward off the shelf, or putting it back.
 *
 * `offered` rather than `active`: the column is called active and the question
 * an operator is answering is whether players can see it, which is not quite
 * the same word. A reward is never deleted — redemptions point at it — so this
 * is the only way one stops being available.
 */
export const setRewardOfferedRequest = z.object({
  offered: z.boolean(),
  reasonCode: z.enum([
    'NEW_CAMPAIGN',
    'SEASONAL_OFFER',
    'PARTNER_PROMOTION',
    'REPLACING_WITHDRAWN_REWARD',
    'COMMERCIAL_DECISION',
    'PRICED_WRONG',
    'OUT_OF_STOCK',
  ]),
  note: z
    .string()
    .min(10, 'Say why — at least 10 characters.')
    .max(500),
});
export type SetRewardOfferedRequest = z.infer<typeof setRewardOfferedRequest>;
